import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Star, Search } from "lucide-react";
import { resizeImageForUpload } from "@/lib/imageResize";
import TmdbChannelArtworkModal from "@/components/TmdbChannelArtworkModal";
import GuideFirstVisitTour from "@/components/guide/GuideFirstVisitTour";

// Read the watch-progress map once. Returning a Map keeps lookups O(1) and the
// component tree re-reads it whenever the guide re-mounts (which happens every
// time it's opened — exactly the lifecycle we want).
function playVhsChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    const tone = (freq, start, dur, gain = 0.035) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      // Sine (was square) — same two-note interval but the harmonics
      // drop off so it reads as a warm bell instead of a buzzy beep.
      // Friendlier on evening / quiet-room listening.
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0, now + start);
      // Slightly longer attack so the tone fades in rather than punches.
      g.gain.linearRampToValueAtTime(gain, now + start + 0.025);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };

    // VHS-style two-note chime: low → high. Both notes pulled to about
    // 40% of the original peak — present but unobtrusive.
    tone(440, 0, 0.18, 0.025);
    tone(659.25, 0.09, 0.24, 0.03);
    // Soft analogue noise tail — dialled from 0.15 → 0.04 gain so it
    // ghosts behind the chime instead of overpowering it.
    const noiseDur = 0.28;
    const buf = ctx.createBuffer(1, ctx.sampleRate * noiseDur, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) {
      ch[i] = (Math.random() * 2 - 1) * 0.04;
    }
    const noise = ctx.createBufferSource();
    const ng = ctx.createGain();
    // Lowpass shaping so the noise reads as tape hiss, not bright fuzz.
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "lowpass";
    noiseFilter.frequency.value = 3500;
    noise.buffer = buf;
    ng.gain.setValueAtTime(0.04, now);
    ng.gain.exponentialRampToValueAtTime(0.0001, now + noiseDur);
    noise.connect(noiseFilter);
    noiseFilter.connect(ng);
    ng.connect(ctx.destination);
    noise.start(now);

    setTimeout(() => ctx.close(), 650);
  } catch (e) {
    /* ignore audio errors */
  }
}

export default function ChannelGuide({
  channels,
  categories,
  videosByChannel,
  currentChannelId,
  currentVideo,
  guideNowSnapshot = [],
  guideScheduleWindow = null,
  onExtendScheduleWindow,
  volume,
  watched,
  watchedTick,
  viewerCounts,
  welcomeBack,
  onWelcomeBackContinue,
  onSelect,
  onClose,
  apiBase,
  isOwner,
  authHeaders,
  onChannelArtChanged,
  onVideosReordered,
  layoutMode,
  onToggleLayoutMode,
  onBrowseChannel,
  onOpenTapeDetail,
}) {
  // Phase 2.3.61e.2-r4 — true DOM player handoff.
  // Publish an intentionally empty DOM host. CRT.js moves the already-running
  // media-plane node into this host and returns the same node home on close.
  const fullGuideLiveHostRef = useRef(null);
  // Phase 2.3.70-r1 — fullscreen EPG width ownership.
  // The schedule canvas used to be sized only from minutes × pixels/minute,
  // which left a large dead region on wide fullscreen displays. Measure the
  // real Guide viewport so fullscreen can expand the proportional EPG canvas
  // to consume all available width without changing bezel geometry.
  const [fullGuideViewportWidth, setFullGuideViewportWidth] = useState(0);
  // Phase 2.3.71-r1.1 - Search temporarily removes the FULL GUIDE list node.
  // Bump this revision when Search clears so the width observer reattaches to
  // the newly mounted fullscreen list rather than remaining on a detached node.
  const [fullGuideMeasureRevision, setFullGuideMeasureRevision] = useState(0);

  // Phase 2.3.70-r2.4 - premium OPENING only.
  // Regression reset: closing deliberately keeps the original immediate
  // onClose lifecycle that was proven playback-safe before r2.
  const [guideSurfaceEntered, setGuideSurfaceEntered] = useState(layoutMode !== "fullscreen");
  const [guideContentReady, setGuideContentReady] = useState(layoutMode !== "fullscreen");

  useEffect(() => {
    const host = fullGuideLiveHostRef.current;
    if (!host) return undefined;

    window.dispatchEvent(new CustomEvent("crt:guide-preview-host", {
      detail: { host },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("crt:guide-preview-host", {
        detail: { host: null },
      }));
    };
  }, [layoutMode]);

  useEffect(() => {
    const list = fullGuideListRef.current;
    if (!list) return undefined;

    const measure = () => {
      const width = Math.max(0, Math.round(list.clientWidth || 0));
      setFullGuideViewportWidth((prev) => (prev === width ? prev : width));
    };

    measure();

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(measure);
      observer.observe(list);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [layoutMode, fullGuideMeasureRevision]);

  useEffect(() => {
    if (layoutMode !== "fullscreen") {
      setGuideSurfaceEntered(true);
      setGuideContentReady(true);
      return undefined;
    }

    setGuideSurfaceEntered(false);
    setGuideContentReady(false);

    let revealTimer = null;
    let rafA = null;
    let rafB = null;

    rafA = requestAnimationFrame(() => {
      rafB = requestAnimationFrame(() => {
        setGuideSurfaceEntered(true);
        revealTimer = window.setTimeout(() => setGuideContentReady(true), 90);
      });
    });

    return () => {
      if (rafA) cancelAnimationFrame(rafA);
      if (rafB) cancelAnimationFrame(rafB);
      if (revealTimer) window.clearTimeout(revealTimer);
    };
  }, [layoutMode]);

  // Owner-curated featured entries — swap into the "Coming Up Next"
  // slot when populated; otherwise the normal programme information remains.
  // fresh on every guide mount; also refetched whenever the owner
  // dispatches `crt:featured-updated` from the FeaturedManager so the
  // preview is immediate after add/edit/delete.
  const [featuredEntries, setFeaturedEntries] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${apiBase}/site/featured`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        setFeaturedEntries(Array.isArray(data.entries) ? data.entries : []);
      } catch (e) { /* silent — normal programme information remains */ }
    };
    load();
    const onChange = () => load();
    window.addEventListener("crt:featured-updated", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("crt:featured-updated", onChange);
    };
  }, [apiBase]);

  // Phase 2.3.61e.6.3 — legacy CHANNELS sidebar state retired.
  // FULL GUIDE's operator shelf now owns channel artwork, station logo,
  // Guide poster and TMDb management directly from selectedFullGuideRow.

  const [layoutPulseTick, setLayoutPulseTick] = useState(0);
  // 250ms scale-pulse on the ⇱/⤢ glyph after a click. Uses a
  // remounting `key` (tick counter) so React re-runs the CSS
  // animation from frame 0 even when the class list doesn't
  // actually change between clicks — cheaper than juggling a
  // setTimeout / classList toggle.
  // Phase 2.3.61e.6.3 — legacy CHANNELS progress/favourite/browser
  // state removed. FULL GUIDE maintains its own selected row/programme state.

  // Phase 2.3.61a-r2 — TV-first Guide navigation foundation.
  // Browsing is non-destructive. Only onSelect commits a real channel tune.
  // Phase 2.3.61e.1-r3 — unified Guide foundation.
  // FULL GUIDE is now the single viewer-facing Guide. Legacy NOW and CHANNELS
  // remain in source temporarily so unique features can be migrated safely.
  // Phase 2.3.61e.6.2 — FULL GUIDE now consumes the live EPG snapshot
  // directly. The retired NOW mode's cursor, refs and compatibility mode state
  // are gone; guideNowSnapshot remains the canonical broadcast timeline input.
  // Phase 2.3.62.4-r3 — merge authoritative NOW rows with r2 schedule rows.
  const fullGuideRows = useMemo(() => {
    const liveRows = Array.isArray(guideNowSnapshot) ? guideNowSnapshot.filter(Boolean) : [];
    const scheduleRows = Array.isArray(guideScheduleWindow?.channels)
      ? guideScheduleWindow.channels.filter(Boolean)
      : [];
    const scheduleById = new Map(scheduleRows.map((item) => [item.id, item]));
    return liveRows
      .filter((row) => row && row.id && !channels.find((c) => c.id === row.id)?.virtual)
      .map((row) => {
        const channel = channels.find((c) => c.id === row.id);
        const playlist = (videosByChannel[row.id] || []).filter((v) => v && !v.is_deleted);
        const schedule = scheduleById.get(row.id) || null;
        const nowIndex = playlist.length
          ? Math.max(0, Math.min(
              Number.isInteger(schedule?.liveVideoIndex)
                ? schedule.liveVideoIndex
                : (Number.isInteger(row.tapeIndex) ? row.tapeIndex : 0),
              playlist.length - 1
            ))
          : 0;
        const programmeBlocks = Array.isArray(schedule?.programmes)
          ? schedule.programmes.map((block) => ({
              ...block,
              video: playlist.find((video) => video?.id === block.video_id) || null,
            }))
          : [];
        return { ...row, channel, playlist, nowIndex, programmeBlocks };
      });
  }, [guideNowSnapshot, guideScheduleWindow, channels, videosByChannel]);

  // Phase 2.3.61c.4 — FULL GUIDE positioning.
  // Initialise from the already-filtered FULL rows, not the raw EPG snapshot,
  // so virtual channels cannot shift the starting row index.
  const [fullGuideRowIndex, setFullGuideRowIndex] = useState(() => {
    const idx = fullGuideRows.findIndex((row) => row && row.id === currentChannelId);
    return idx >= 0 ? idx : 0;
  });
  const [fullGuideOffset, setFullGuideOffset] = useState(0);
  // Phase 2.3.61e.3 — programme/episode discovery inside FULL GUIDE.
  // This deliberately uses FULL GUIDE's own playlist selection rather than
  // the retired CHANNELS sidebar, keeping discovery non-destructive.
  // Phase 2.3.61f.2 — Smart Discovery Strip.
  // One physical discovery rail is always available. FEATURED is the normal
  // landing state; a deliberate PROGRAMMES choice persists for this Guide
  // session and automatically follows whichever channel the viewer highlights.
  const [fullGuideDiscoveryMode, setFullGuideDiscoveryMode] = useState("featured");
  // Phase 2.3.83 — PROGRAMMES series → episodes browser.
  // null means the multi-series selector is being shown. Single-series
  // programmes bypass that selector automatically.
  const [fullGuideProgrammeSeries, setFullGuideProgrammeSeries] = useState(null);
  const fullGuideFeaturedOpen =
    fullGuideDiscoveryMode === "featured" && featuredEntries.length > 0;
  const fullGuideDiscoveryOpen =
    fullGuideDiscoveryMode === "programmes" ||
    (fullGuideDiscoveryMode === "featured" && featuredEntries.length === 0);
  // Phase 2.3.61e.5 — operator controls migrate into FULL GUIDE without
  // reusing any legacy CHANNELS sidebar state. Owner-only and collapsed
  // by default so viewers see exactly the accepted Guide layout.
  const [fullGuideOperatorOpen, setFullGuideOperatorOpen] = useState(false);
  const [fullGuideTmdbArtworkOpen, setFullGuideTmdbArtworkOpen] = useState(false);
  const fullGuideOperatorArtInputRef = useRef(null);
  const fullGuideOperatorLogoInputRef = useRef(null);
  const [fullGuideOperatorUploading, setFullGuideOperatorUploading] = useState(false);
  const [fullGuideOperatorSaving, setFullGuideOperatorSaving] = useState(null);
  const [fullGuideOperatorDraft, setFullGuideOperatorDraft] = useState({
    thumbnail: "",
    logo: "",
    poster: "",
  });
  const fullGuideDiscoveryRefs = useRef(new Map());
  const fullGuideRowRefs = useRef(new Map());
  const fullGuideCellRefs = useRef(new Map());
  const fullGuideListRef = useRef(null);
  // Phase 2.3.62.6-r1 — one edge expansion at a time.
  const fullGuideWindowExtendBusyRef = useRef(false);
  const fullGuideWindowExtendPendingRef = useRef(null);
  // Phase 2.3.62.6-r1.1 — fresh Guide/current-channel orientation must not
  // rerun merely because the 30-second schedule refresh rebuilds fullGuideRows.
  const fullGuideInitialOrientationChannelRef = useRef(undefined);
  const fullGuideScrollReadyRef = useRef(false);
  const fullGuideHorizontalReadyRef = useRef(false);
  // Phase 2.3.62.4-r4 — a time cursor makes FULL GUIDE genuinely two-dimensional.
  // null means "orient from the live/current programme"; deliberate EPG movement
  // stores a real UTC millisecond anchor which ↑/↓ preserve across channels.
  const [fullGuideTimeAnchorMs, setFullGuideTimeAnchorMs] = useState(null);

  const selectedFullGuideRow =
    fullGuideRows.length > 0
      ? fullGuideRows[Math.max(0, Math.min(fullGuideRowIndex, fullGuideRows.length - 1))]
      : null;

  const selectedFullGuideIndex = (() => {
    if (!selectedFullGuideRow?.playlist?.length) return 0;
    const total = selectedFullGuideRow.playlist.length;
    return ((selectedFullGuideRow.nowIndex + fullGuideOffset) % total + total) % total;
  })();

  const selectedFullGuideVideo =
    selectedFullGuideRow?.playlist?.[selectedFullGuideIndex] || null;

  // Phase 2.3.90 — when FEATURED is the active discovery surface, keep the
  // information panel about the Featured pick rather than the live-preview
  // episode. Channel-level Featured entries intentionally have no video id.
  const selectedFullGuideFeaturedEntry =
    fullGuideFeaturedOpen
      ? (
          featuredEntries.find((entry) => {
            if (!entry?.target_channel_id || entry.target_channel_id !== selectedFullGuideRow?.id) {
              return false;
            }
            if (!entry?.target_video_id) return true;
            return entry.target_video_id === selectedFullGuideVideo?.id;
          }) || null
        )
      : null;

  const selectedFullGuideFeaturedSynopsis = (() => {
    if (!selectedFullGuideFeaturedEntry) return "";

    if (selectedFullGuideFeaturedEntry.tagline) {
      return selectedFullGuideFeaturedEntry.tagline;
    }

    if (selectedFullGuideFeaturedEntry.target_video_id) {
      const featuredVideo =
        selectedFullGuideRow?.playlist?.find(
          (video) => video?.id === selectedFullGuideFeaturedEntry.target_video_id
        ) || null;

      const videoSynopsis =
        featuredVideo?.description ||
        featuredVideo?.overview ||
        featuredVideo?.synopsis ||
        featuredVideo?.summary;

      if (videoSynopsis) return String(videoSynopsis).trim();
    }

    return String(
      selectedFullGuideRow?.channel?.description ||
      "Featured on scartlead.tv."
    ).trim();
  })();

  // Phase 2.3.83 — only enable the series hierarchy when every programme
  // has trustworthy structured season metadata. If even one item does not,
  // retain the established all-programmes rail so nothing becomes hidden.
  const fullGuideProgrammeSeriesGroups = useMemo(() => {
    const playlist = Array.isArray(selectedFullGuideRow?.playlist)
      ? selectedFullGuideRow.playlist
      : [];

    if (!playlist.length) return [];

    const grouped = new Map();

    for (const video of playlist) {
      if (video?.season_number == null) return [];

      const seriesNumber = Number(video.season_number);
      if (!Number.isInteger(seriesNumber) || seriesNumber < 0) return [];

      if (!grouped.has(seriesNumber)) grouped.set(seriesNumber, []);
      grouped.get(seriesNumber).push(video);
    }

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a - b)
      .map(([seriesNumber, videos]) => ({
        seriesNumber,
        videos,
      }));
  }, [selectedFullGuideRow?.playlist]);

  const fullGuideProgrammeSeriesEnabled =
    fullGuideProgrammeSeriesGroups.length > 0;

  const fullGuideProgrammeNeedsSeriesChoice =
    fullGuideProgrammeSeriesGroups.length > 1;

  const fullGuideProgrammeRequestedSeriesExists =
    fullGuideProgrammeSeriesGroups.some(
      (group) => String(group.seriesNumber) === String(fullGuideProgrammeSeries)
    );

  const fullGuideProgrammeEffectiveSeries =
    fullGuideProgrammeSeriesEnabled
      ? (
          fullGuideProgrammeNeedsSeriesChoice
            ? (
                fullGuideProgrammeRequestedSeriesExists
                  ? fullGuideProgrammeSeries
                  : null
              )
            : fullGuideProgrammeSeriesGroups[0]?.seriesNumber ?? null
        )
      : null;

  const fullGuideProgrammeShowSeriesSelector =
    fullGuideProgrammeSeriesEnabled &&
    fullGuideProgrammeNeedsSeriesChoice &&
    fullGuideProgrammeEffectiveSeries == null;

  const fullGuideProgrammeVisibleVideos = (
    Array.isArray(selectedFullGuideRow?.playlist)
      ? selectedFullGuideRow.playlist
      : []
  )
    .map((video, index) => ({ video, index }))
    .filter(({ video }) => (
      !fullGuideProgrammeSeriesEnabled ||
      fullGuideProgrammeEffectiveSeries == null ||
      String(video?.season_number) === String(fullGuideProgrammeEffectiveSeries)
    ));

  // Changing channel or leaving/re-entering PROGRAMMES starts from the
  // appropriate top level again. Single-series shows still auto-open.
  useEffect(() => {
    setFullGuideProgrammeSeries(null);
  }, [selectedFullGuideRow?.id, fullGuideDiscoveryMode]);

  const fullGuideOperatorChannel = selectedFullGuideRow?.channel || null;

  useEffect(() => {
    if (!fullGuideOperatorOpen || !fullGuideOperatorChannel) return;
    setFullGuideOperatorDraft({
      thumbnail: fullGuideOperatorChannel.thumbnail_url || "",
      logo: fullGuideOperatorChannel.logo_url || "",
      poster: fullGuideOperatorChannel.guide_poster_url || "",
    });
  }, [
    fullGuideOperatorOpen,
    fullGuideOperatorChannel?.id,
    fullGuideOperatorChannel?.thumbnail_url,
    fullGuideOperatorChannel?.logo_url,
    fullGuideOperatorChannel?.guide_poster_url,
  ]);

  const saveFullGuideOperatorField = async (field) => {
    const channel = fullGuideOperatorChannel;
    if (!isOwner || !channel) return;

    setFullGuideOperatorSaving(field);
    try {
      let endpoint;
      let body;

      if (field === "thumbnail") {
        const url = (fullGuideOperatorDraft.thumbnail || "").trim();
        if (channel.isSubChannel) {
          endpoint = `${apiBase}/channels/${channel.parent_id}/show-art/link`;
          body = { show_title: channel.show_title, url: url || null };
        } else {
          endpoint = `${apiBase}/channels/${channel.id}/thumbnail/link`;
          body = { url: url || null };
        }
        await axios.post(endpoint, body, {
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        });
        toast.success(url ? "CHANNEL ART LINKED" : "CHANNEL ART LINK CLEARED");
      } else if (field === "logo") {
        if (channel.isSubChannel) {
          throw new Error("Station Logo is managed on the parent channel.");
        }
        const url = (fullGuideOperatorDraft.logo || "").trim();
        endpoint = `${apiBase}/channels/${channel.id}/logo/link`;
        body = { url: url || null };
        await axios.post(endpoint, body, {
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        });
        toast.success(url ? "STATION LOGO LINKED" : "STATION LOGO CLEARED");
      } else if (field === "poster") {
        const url = (fullGuideOperatorDraft.poster || "").trim();
        if (channel.isSubChannel) {
          endpoint = `${apiBase}/channels/${channel.parent_id}/show-art/link`;
          body = { show_title: channel.show_title, url: url || null };
        } else {
          endpoint = `${apiBase}/channels/${channel.id}/guide_poster/link`;
          body = { url: url || null };
        }
        await axios.post(endpoint, body, {
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        });
        toast.success(url ? "GUIDE POSTER LINKED" : "GUIDE POSTER CLEARED");
      } else {
        throw new Error(`Unknown operator field: ${field}`);
      }

      if (onChannelArtChanged) await onChannelArtChanged();
    } catch (err) {
      toast.error("SAVE FAILED", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setFullGuideOperatorSaving(null);
    }
  };

  // Phase 2.3.61e.4 — move FULL GUIDE selection to a curated target without
  // tuning playback. WATCH NOW remains the only explicit playback commit.
  const selectFullGuideTarget = (channelId, videoId = null, fallbackIndex = 0) => {
    if (!channelId) return false;
    const rowIndex = fullGuideRows.findIndex((row) => row?.id === channelId);
    if (rowIndex < 0) return false;

    const row = fullGuideRows[rowIndex];
    const playlist = Array.isArray(row?.playlist) ? row.playlist : [];
    let targetIndex = Math.max(0, Math.min(Number.isInteger(fallbackIndex) ? fallbackIndex : 0, Math.max(0, playlist.length - 1)));

    if (videoId && playlist.length) {
      const exactIndex = playlist.findIndex((video) => video?.id === videoId);
      if (exactIndex >= 0) targetIndex = exactIndex;
    }

    setFullGuideRowIndex(rowIndex);
    setFullGuideOffset(targetIndex - (Number.isInteger(row?.nowIndex) ? row.nowIndex : 0));
    setFullGuideTimeAnchorMs(null);
    return true;
  };

  useEffect(() => {
    if (
      !fullGuideDiscoveryOpen ||
      fullGuideProgrammeShowSeriesSelector ||
      !selectedFullGuideVideo?.id
    ) return;

    const node = fullGuideDiscoveryRefs.current.get(selectedFullGuideVideo.id);
    if (!node?.scrollIntoView) return;

    node.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [
    fullGuideDiscoveryOpen,
    fullGuideProgrammeShowSeriesSelector,
    selectedFullGuideRow?.id,
    selectedFullGuideVideo?.id,
  ]);

  // Phase 2.3.62.4-r3 — real-time proportional EPG geometry.
  const EPG_CHANNEL_WIDTH_PX = 192;
  const EPG_MINUTE_WIDTH_PX = 8;
  const epgWindowStartMs = Date.parse(guideScheduleWindow?.window_start || "");
  const epgWindowEndMs = Date.parse(guideScheduleWindow?.window_end || "");
  const epgServerMs = Date.parse(guideScheduleWindow?.server_time || "");
  const epgWindowValid =
    Number.isFinite(epgWindowStartMs) &&
    Number.isFinite(epgWindowEndMs) &&
    epgWindowEndMs > epgWindowStartMs;
  const epgWindowMinutes = epgWindowValid
    ? (epgWindowEndMs - epgWindowStartMs) / 60000
    : 150;

  // Natural schedule geometry is unchanged: every minute still receives the
  // established 8px baseline. FULLSCREEN alone may widen that canvas to the
  // available viewport so the EPG reaches the right edge instead of ending
  // halfway across the screen. If extended schedule data is naturally wider
  // than the viewport, natural width wins and normal horizontal scrolling
  // remains intact. Bezel mode deliberately retains the 2.3.69 calculation.
  const epgNaturalCanvasWidthPx = Math.max(
    720,
    epgWindowMinutes * EPG_MINUTE_WIDTH_PX
  );
  const epgFullscreenFillWidthPx = Math.max(
    720,
    fullGuideViewportWidth - EPG_CHANNEL_WIDTH_PX
  );
  const epgCanvasWidthPx =
    layoutMode === "fullscreen"
      ? Math.max(epgNaturalCanvasWidthPx, epgFullscreenFillWidthPx)
      : epgNaturalCanvasWidthPx;

  const [epgClockTick, setEpgClockTick] = useState(() => Date.now());
  const epgFetchedClientMsRef = useRef(Date.now());

  useEffect(() => {
    epgFetchedClientMsRef.current = Date.now();
  }, [guideScheduleWindow?.server_time]);

  useEffect(() => {
    const timer = setInterval(() => setEpgClockTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const epgNowMs = Number.isFinite(epgServerMs)
    ? epgServerMs + Math.max(0, epgClockTick - epgFetchedClientMsRef.current)
    : epgClockTick;

  const epgNowLeftPx = epgWindowValid
    ? Math.max(
        0,
        Math.min(
          epgCanvasWidthPx,
          ((epgNowMs - epgWindowStartMs) / (epgWindowEndMs - epgWindowStartMs)) * epgCanvasWidthPx
        )
      )
    : 0;

  const epgClockTicks = useMemo(() => {
    if (!epgWindowValid) return [];
    const ticks = [];
    const stepMs = 30 * 60 * 1000;
    let cursor = Math.ceil(epgWindowStartMs / stepMs) * stepMs;
    let previousDateKey = null;

    while (cursor <= epgWindowEndMs) {
      const tickDate = new Date(cursor);
      const dateKey = `${tickDate.getFullYear()}-${tickDate.getMonth()}-${tickDate.getDate()}`;
      const dayBoundary = previousDateKey !== null && dateKey !== previousDateKey;

      ticks.push({
        ms: cursor,
        leftPx:
          ((cursor - epgWindowStartMs) / (epgWindowEndMs - epgWindowStartMs)) * epgCanvasWidthPx,
        label: tickDate.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        dayBoundary,
        dayLabel: dayBoundary
          ? tickDate.toLocaleDateString([], {
              weekday: "short",
              day: "2-digit",
              month: "short",
            }).toUpperCase()
          : null,
      });

      previousDateKey = dateKey;
      cursor += stepMs;
    }
    return ticks;
  }, [epgWindowStartMs, epgWindowEndMs, epgCanvasWidthPx, epgWindowValid]);

  const fullGuideTimelineFor = (row) => {
    if (!epgWindowValid || !Array.isArray(row?.programmeBlocks)) return [];
    const total = Math.max(1, row.playlist?.length || 1);

    return row.programmeBlocks
      .map((block) => {
        const startMs = Date.parse(block.programme_start || "");
        const endMs = Date.parse(block.programme_end || "");
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;

        const clippedStart = Math.max(startMs, epgWindowStartMs);
        const clippedEnd = Math.min(endMs, epgWindowEndMs);
        if (clippedEnd <= clippedStart) return null;

        const leftPx =
          ((clippedStart - epgWindowStartMs) / (epgWindowEndMs - epgWindowStartMs)) * epgCanvasWidthPx;
        const widthPx = Math.max(
          18,
          ((clippedEnd - clippedStart) / (epgWindowEndMs - epgWindowStartMs)) * epgCanvasWidthPx
        );

        const index = Number.isInteger(block.video_index) ? block.video_index : 0;
        let absoluteOffset = index - row.nowIndex;
        if (absoluteOffset > total / 2) absoluteOffset -= total;
        if (absoluteOffset < -(total / 2)) absoluteOffset += total;

        return {
          ...block,
          index,
          absoluteOffset,
          video: block.video || row.playlist?.[index] || null,
          leftPx,
          widthPx,
        };
      })
      .filter(Boolean);
  };

  // Phase 2.3.62.4-r4 — programme-aware EPG selection/navigation.
  const fullGuideProgrammeKey = (row, cell) =>
    row && cell
      ? `${row.id}::${cell.programme_start || ""}::${cell.programme_end || ""}::${cell.index}`
      : null;

  const fullGuideProgrammeMidpointMs = (cell) => {
    const startMs = Date.parse(cell?.programme_start || "");
    const endMs = Date.parse(cell?.programme_end || "");
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      return startMs + ((endMs - startMs) / 2);
    }
    return null;
  };

  const fullGuideProgrammeAtTime = (row, anchorMs) => {
    const timeline = fullGuideTimelineFor(row);
    if (!timeline.length) return null;

    if (Number.isFinite(anchorMs)) {
      const containing = timeline.find((cell) => {
        const startMs = Date.parse(cell.programme_start || "");
        const endMs = Date.parse(cell.programme_end || "");
        return Number.isFinite(startMs) &&
          Number.isFinite(endMs) &&
          anchorMs >= startMs &&
          anchorMs < endMs;
      });
      if (containing) return containing;

      return timeline.reduce((best, cell) => {
        const mid = fullGuideProgrammeMidpointMs(cell);
        if (!Number.isFinite(mid)) return best;
        if (!best) return cell;
        const bestMid = fullGuideProgrammeMidpointMs(best);
        return Math.abs(mid - anchorMs) < Math.abs(bestMid - anchorMs) ? cell : best;
      }, null);
    }

    return timeline.find((cell) => cell.is_live_now) ||
      timeline.find((cell) => cell.index === selectedFullGuideIndex) ||
      timeline[0];
  };

  const selectedFullGuideTimeline = selectedFullGuideRow
    ? fullGuideTimelineFor(selectedFullGuideRow)
    : [];

  const selectedFullGuideProgramme = (() => {
    if (!selectedFullGuideTimeline.length) return null;

    if (Number.isFinite(fullGuideTimeAnchorMs)) {
      return fullGuideProgrammeAtTime(selectedFullGuideRow, fullGuideTimeAnchorMs);
    }

    if (fullGuideOffset === 0) {
      const liveCell = selectedFullGuideTimeline.find((cell) => cell.is_live_now);
      if (liveCell) return liveCell;
    }

    return selectedFullGuideTimeline.find((cell) => cell.index === selectedFullGuideIndex) ||
      selectedFullGuideTimeline[0];
  })();

  const selectedFullGuideProgrammeKey =
    fullGuideProgrammeKey(selectedFullGuideRow, selectedFullGuideProgramme);

  const selectFullGuideProgramme = (rowIndex, cell, updateAnchor = true) => {
    const row = fullGuideRows[rowIndex];
    if (!row || !cell) return false;
    setFullGuideRowIndex(rowIndex);
    setFullGuideOffset(cell.absoluteOffset);
    if (updateAnchor) {
      const midpoint = fullGuideProgrammeMidpointMs(cell);
      setFullGuideTimeAnchorMs(Number.isFinite(midpoint) ? midpoint : null);
    }
    return true;
  };

  const selectFullGuideRowAtTime = (rowIndex, anchorMs) => {
    if (!fullGuideRows.length) return false;
    const boundedIndex = Math.max(0, Math.min(fullGuideRows.length - 1, rowIndex));
    const row = fullGuideRows[boundedIndex];
    const target = fullGuideProgrammeAtTime(row, anchorMs);
    if (!target) {
      setFullGuideRowIndex(boundedIndex);
      return false;
    }
    setFullGuideRowIndex(boundedIndex);
    setFullGuideOffset(target.absoluteOffset);
    setFullGuideTimeAnchorMs(Number.isFinite(anchorMs) ? anchorMs : fullGuideProgrammeMidpointMs(target));
    return true;
  };

  // Phase 2.3.62.6-r2 - Guide usability around the extended time canvas.
  // BACK TO NOW is a time-axis action: keep the highlighted channel, jump that
  // row to the programme airing at the authoritative live clock, and re-centre.
  const selectedFullGuideProgrammeContainsNow = (() => {
    const startMs = Date.parse(selectedFullGuideProgramme?.programme_start || "");
    const endMs = Date.parse(selectedFullGuideProgramme?.programme_end || "");
    return Number.isFinite(startMs) &&
      Number.isFinite(endMs) &&
      epgNowMs >= startMs &&
      epgNowMs < endMs;
  })();

  const epgLoadedPastMinutes = epgWindowValid
    ? Math.max(0, Math.round((epgServerMs - epgWindowStartMs) / 60000))
    : 30;
  const epgLoadedFutureMinutes = epgWindowValid
    ? Math.max(0, Math.round((epgWindowEndMs - epgServerMs) / 60000))
    : 120;
  const epgExtendedPast = epgLoadedPastMinutes >= 60;
  const epgExtendedFuture = epgLoadedFutureMinutes >= 180;
  const epgExtended = epgExtendedPast || epgExtendedFuture;

  const formatGuideRangeMinutes = (minutes) => {
    const value = Math.max(0, Number(minutes) || 0);
    if (value >= 60 && value % 60 === 0) return `${value / 60}h`;
    if (value >= 60) return `${Math.floor(value / 60)}h ${value % 60}m`;
    return `${value}m`;
  };

  const returnFullGuideToNow = () => {
    if (!fullGuideRows.length) return;

    const rowIndex = Math.max(0, Math.min(
      fullGuideRows.length - 1,
      Number.isInteger(fullGuideRowIndex) ? fullGuideRowIndex : 0
    ));
    const row = fullGuideRows[rowIndex];
    const target = fullGuideProgrammeAtTime(row, epgNowMs);
    if (!target) return;

    fullGuideHorizontalReadyRef.current = false;
    setFullGuideRowIndex(rowIndex);
    setFullGuideOffset(target.absoluteOffset);
    setFullGuideTimeAnchorMs(epgNowMs);
  };

  // Compact programme-title hierarchy for FULL GUIDE cells.
  // Common "Show S01E04 - Episode Title" strings become a small show/episode
  // line plus a stronger episode title; everything else falls back cleanly.
  const fullGuideProgrammeParts = (video) => {
    const raw = String(video?.title || "").trim();
    if (!raw) return { eyebrow: "", title: "— STAND BY —" };

    const match = raw.match(/^(.*?)\s+(S\d{1,2}E\d{1,3})\s*[-–:]\s*(.+)$/i);
    if (match) {
      return {
        eyebrow: `${match[1].trim()} · ${match[2].toUpperCase()}`,
        title: match[3].trim(),
      };
    }

    return { eyebrow: "", title: raw };
  };

  const fullGuideChannelLogoFor = (row) =>
    row?.channel?.logo_url ||
    row?.channel?.thumbnail_url ||
    row?.channel?.guide_poster_url ||
    null;

  const fullGuideProgrammeSlotLabel = (cell) => {
    const start = new Date(cell?.programme_start || "");
    const end = new Date(cell?.programme_end || "");
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const fmt = (date) =>
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return `${fmt(start)} – ${fmt(end)}`;
    }
    return cell?.is_live_now ? "NOW PLAYING" : "PROGRAMME";
  };

  // Phase 2.3.62.6-r3 - selected schedule date context.
  const fullGuideProgrammeDateContext = (cell) => {
    const selected = new Date(cell?.programme_start || "");
    const now = new Date(epgNowMs);
    if (Number.isNaN(selected.getTime()) || Number.isNaN(now.getTime())) return "";

    const startOf = (date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const diffDays = Math.round((startOf(selected) - startOf(now)) / 86400000);

    if (diffDays === 0) return "TODAY";
    if (diffDays === 1) return "TOMORROW";
    if (diffDays === -1) return "YESTERDAY";

    return selected.toLocaleDateString([], {
      weekday: "short",
      day: "2-digit",
      month: "short",
    }).toUpperCase();
  };

  const selectedFullGuideDateContext =
    fullGuideProgrammeDateContext(selectedFullGuideProgramme);

  // Phase 2.3.61e.6.2 — the old debounced Guide preview-player target
  // was retired. FULL GUIDE now hosts the actual live CRT player for true PVR
  // continuity, so no secondary preview stream is selected here.

  useEffect(() => {
    if (!fullGuideRows.length) return;
    if (fullGuideInitialOrientationChannelRef.current === currentChannelId) return;
    fullGuideInitialOrientationChannelRef.current = currentChannelId;

    const idx = fullGuideRows.findIndex((row) => row.id === currentChannelId);
    const nextIndex = idx >= 0 ? idx : 0;
    setFullGuideRowIndex(nextIndex);
    setFullGuideOffset(0);
    setFullGuideTimeAnchorMs(null);
    fullGuideHorizontalReadyRef.current = false;

    // Phase 2.3.61f.3 — orient the viewer immediately: the actually-playing
    // LIVE channel opens in the visual middle of the Guide, not at an edge.
    requestAnimationFrame(() => {
      const list = fullGuideListRef.current;
      const liveRow = fullGuideRowRefs.current.get(fullGuideRows[nextIndex]?.id);
      if (!list || !liveRow) return;
      const header = list.querySelector('[data-testid="guide-full-header"]');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const rowTop = liveRow.offsetTop;
      const rowHeight = liveRow.offsetHeight;
      const visibleHeight = Math.max(0, list.clientHeight - headerHeight);
      list.scrollTop = Math.max(0, rowTop - headerHeight - ((visibleHeight - rowHeight) / 2));
    });
  }, [currentChannelId, fullGuideRows]);

  useEffect(() => {
    if (!selectedFullGuideRow?.id) return;

    const list = fullGuideListRef.current;
    const row = fullGuideRowRefs.current.get(selectedFullGuideRow.id);
    if (!list || !row) return;

    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const header = list.querySelector('[data-testid="guide-full-header"]');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;

    const visibleTop = listRect.top + headerHeight;
    const visibleHeight = Math.max(0, list.clientHeight - headerHeight);
    const targetCenter = visibleTop + (visibleHeight / 2);
    const rowCenter = rowRect.top + (rowRect.height / 2);
    const delta = rowCenter - targetCenter;

    list.scrollTo({
      top: Math.max(0, list.scrollTop + delta),
      behavior: fullGuideScrollReadyRef.current ? "smooth" : "auto",
    });
    fullGuideScrollReadyRef.current = true;
  }, [selectedFullGuideRow?.id]);

  // Phase 2.3.62.4-r4 — keep the selected scheduled programme visible in
  // the same overflow viewport as the clock ruler and every channel row.
  useEffect(() => {
    if (!selectedFullGuideProgrammeKey) return undefined;

    const raf = requestAnimationFrame(() => {
      const list = fullGuideListRef.current;
      const cell = fullGuideCellRefs.current.get(selectedFullGuideProgrammeKey);
      if (!list || !cell) return;

      const listRect = list.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      const visibleLeft = listRect.left + EPG_CHANNEL_WIDTH_PX;
      const visibleRight = listRect.right;
      const visibleWidth = Math.max(0, visibleRight - visibleLeft);
      if (visibleWidth <= 0) return;

      if (!fullGuideHorizontalReadyRef.current) {
        const cellCenter = cellRect.left + (cellRect.width / 2);
        const targetCenter = visibleLeft + (visibleWidth / 2);
        list.scrollTo({
          left: Math.max(0, list.scrollLeft + (cellCenter - targetCenter)),
          behavior: "auto",
        });
        fullGuideHorizontalReadyRef.current = true;
        return;
      }

      const margin = 24;
      let delta = 0;
      if (cellRect.left < visibleLeft + margin) {
        delta = cellRect.left - (visibleLeft + margin);
      } else if (cellRect.right > visibleRight - margin) {
        delta = cellRect.right - (visibleRight - margin);
      }

      if (Math.abs(delta) >= 1) {
        list.scrollTo({
          left: Math.max(0, list.scrollLeft + delta),
          behavior: "smooth",
        });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [selectedFullGuideProgrammeKey]);

  // Phase 2.3.62.6-r1 — once the enlarged authoritative window arrives,
  // relocate the old edge programme by its stable schedule key and advance
  // exactly one real programme beyond it.
  useEffect(() => {
    const pending = fullGuideWindowExtendPendingRef.current;
    if (!pending) return;

    const rowIndex = fullGuideRows.findIndex((row) => row?.id === pending.rowId);
    if (rowIndex < 0) {
      fullGuideWindowExtendPendingRef.current = null;
      fullGuideWindowExtendBusyRef.current = false;
      return;
    }

    const row = fullGuideRows[rowIndex];
    const timeline = fullGuideTimelineFor(row);
    const oldIndex = timeline.findIndex(
      (cell) => fullGuideProgrammeKey(row, cell) === pending.programmeKey
    );
    if (oldIndex < 0) {
      fullGuideWindowExtendPendingRef.current = null;
      fullGuideWindowExtendBusyRef.current = false;
      return;
    }

    const targetIndex = pending.direction === "left" ? oldIndex - 1 : oldIndex + 1;
    if (targetIndex < 0 || targetIndex >= timeline.length) {
      fullGuideWindowExtendPendingRef.current = null;
      fullGuideWindowExtendBusyRef.current = false;
      return;
    }

    fullGuideWindowExtendPendingRef.current = null;
    fullGuideWindowExtendBusyRef.current = false;
    selectFullGuideProgramme(rowIndex, timeline[targetIndex], true);
  }, [
    guideScheduleWindow?.window_start,
    guideScheduleWindow?.window_end,
  ]);

  // ─── Search ─────────────────────────────────────────────────────────
  // Live-filter across channels + tapes. Case-insensitive substring,
  // grouped in the dropdown (channels first, then tapes) and capped so
  // a broad match doesn't render 500 rows. Everything is already in
  // client state (the Guide has been sitting on `channels` and
  // `videosByChannel` for its rail rendering), so this is free — no
  // backend endpoint or debounce needed at any realistic library size.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchIndex, setSearchIndex] = useState(0);
  const searchInputRef = useRef(null);

  // Phase 2.3.62.6-r4 - transient Guide navigation feedback only.
  const [guideNavPulse, setGuideNavPulse] = useState(null);
  const [guideEdgeFeedback, setGuideEdgeFeedback] = useState(null);
  const guideNavPulseTimerRef = useRef(null);
  const guideEdgeFeedbackTimerRef = useRef(null);

  const flashGuideNavigation = (direction) => {
    if (guideNavPulseTimerRef.current) clearTimeout(guideNavPulseTimerRef.current);
    setGuideNavPulse(direction);
    guideNavPulseTimerRef.current = setTimeout(() => {
      setGuideNavPulse(null);
      guideNavPulseTimerRef.current = null;
    }, 220);
  };

  const flashGuideEdge = (direction) => {
    if (guideEdgeFeedbackTimerRef.current) clearTimeout(guideEdgeFeedbackTimerRef.current);
    setGuideEdgeFeedback(direction);
    guideEdgeFeedbackTimerRef.current = setTimeout(() => {
      setGuideEdgeFeedback(null);
      guideEdgeFeedbackTimerRef.current = null;
    }, 1100);
  };

  useEffect(() => () => {
    if (guideNavPulseTimerRef.current) clearTimeout(guideNavPulseTimerRef.current);
    if (guideEdgeFeedbackTimerRef.current) clearTimeout(guideEdgeFeedbackTimerRef.current);
  }, []);
  const searchQ = searchQuery.trim().toLowerCase();

  // Search and FULL GUIDE are mutually rendered. When Search clears, FULL
  // GUIDE's list is recreated in this render. Trigger the existing width-owner
  // effect so ResizeObserver attaches to that new node and measures fullscreen
  // geometry again.
  useEffect(() => {
    if (searchQ.length >= 1) return;
    setFullGuideMeasureRevision((revision) => revision + 1);
  }, [searchQ]);

  const searchResults = useMemo(() => {
    if (searchQ.length < 1) return { channels: [], videos: [], flat: [] };
    // Virtual channels join real ones — the "SEARCH" affordance should
    // find FAVOURITES / CONTACT by name just like any other rail.
    const chMatches = channels
      .filter((c) => (c.name || "").toLowerCase().includes(searchQ))
      .slice(0, 12)
      .map((c) => ({ kind: "channel", id: c.id, channel: c }));
    // For videos, we also need the channel context to show a subtitle
    // and to know which channel to tune to. Skip deleted rows.
    const vidMatches = [];
    for (const ch of channels) {
      const list = videosByChannel[ch.id] || [];
      list.forEach((v, i) => {
        if (v.is_deleted) return;
        if (!(v.title || "").toLowerCase().includes(searchQ)) return;
        vidMatches.push({
          kind: "video",
          id: `${ch.id}::${v.id}`,
          channel: ch,
          video: v,
          index: i,
        });
      });
      if (vidMatches.length >= 60) break; // hard cap — 60 tapes is plenty
    }
    const flat = [...chMatches, ...vidMatches];
    return { channels: chMatches, videos: vidMatches.slice(0, 60), flat: flat.slice(0, 72) };
  }, [searchQ, channels, videosByChannel]);

  // Phase 2.3.71-r1b — Premium Search Foundation.
  // Search matching and tune behaviour remain unchanged. These helpers only
  // present the already-loaded result objects as a richer discovery surface.
  const searchActiveHit = searchResults.flat[searchIndex] || null;

  const searchHitTitle = (hit) =>
    hit?.video?.title || hit?.channel?.name || "UNTITLED";

  const searchHitType = (hit) =>
    hit?.kind === "video" ? "PROGRAMME" : "CHANNEL";

  const searchHitArtwork = (hit, backdrop = false) => {
    const video = hit?.video;
    const channel = hit?.channel;

    if (video) {
      if (backdrop && video.backdrop_url) return video.backdrop_url;
      if (video.poster_url) return video.poster_url;
      if (video.backdrop_url) return video.backdrop_url;
    }

    if (channel) {
      if (channel.guide_poster_url) return channel.guide_poster_url;
      if (channel.thumbnail_url) return channel.thumbnail_url;
    }

    return null;
  };

  const searchHitMeta = (hit) => {
    if (!hit) return [];

    if (hit.kind === "channel") {
      return [
        hit.channel?.virtual
          ? (hit.channel?.numberLabel || "VIRTUAL")
          : (hit.channel?.number != null ? `CH ${String(hit.channel.number).padStart(2, "0")}` : null),
        hit.channel?.genre || hit.channel?.category || null,
      ].filter(Boolean);
    }

    return [
      hit.video?.year || null,
      hit.video?.genre || null,
      hit.channel?.name || null,
      hit.video?.kind ? String(hit.video.kind).toUpperCase() : null,
    ].filter(Boolean);
  };

  const searchHitDescription = (hit) =>
    hit?.video?.description ||
    hit?.video?.overview ||
    hit?.channel?.description ||
    "No synopsis is available for this result yet.";


  // Reset the highlighted result when the query changes so the up/down
  // arrows and Enter land on the top match after every keystroke.
  useEffect(() => { setSearchIndex(0); }, [searchQ]);

  // Phase 2.3.61c.1 — FULL GUIDE owns four-way navigation.
  useEffect(() => {
    if (searchQ.length >= 1) return undefined;

    const moveFullGuide = (direction) => {
      if (!fullGuideRows.length || !selectedFullGuideRow) return;

      const selectedTimeline = fullGuideTimelineFor(selectedFullGuideRow);
      const currentCell =
        selectedFullGuideProgramme ||
        fullGuideProgrammeAtTime(
          selectedFullGuideRow,
          Number.isFinite(fullGuideTimeAnchorMs) ? fullGuideTimeAnchorMs : epgNowMs
        );

      if (direction === "left" || direction === "right") {
        if (!selectedTimeline.length || !currentCell) return;
        const currentKey = fullGuideProgrammeKey(selectedFullGuideRow, currentCell);
        const currentIndex = selectedTimeline.findIndex(
          (cell) => fullGuideProgrammeKey(selectedFullGuideRow, cell) === currentKey
        );
        if (currentIndex < 0) return;

        const nextIndex = direction === "left"
          ? Math.max(0, currentIndex - 1)
          : Math.min(selectedTimeline.length - 1, currentIndex + 1);
        if (nextIndex === currentIndex) {
          if (!onExtendScheduleWindow || fullGuideWindowExtendBusyRef.current) return;

          fullGuideWindowExtendBusyRef.current = true;
          flashGuideEdge(direction);
          fullGuideWindowExtendPendingRef.current = {
            direction,
            rowId: selectedFullGuideRow.id,
            programmeKey: currentKey,
          };

          Promise.resolve(onExtendScheduleWindow(direction))
            .then((result) => {
              if (!result?.expanded) {
                fullGuideWindowExtendPendingRef.current = null;
                fullGuideWindowExtendBusyRef.current = false;
              }
            })
            .catch(() => {
              fullGuideWindowExtendPendingRef.current = null;
              fullGuideWindowExtendBusyRef.current = false;
            });
          return;
        }

        selectFullGuideProgramme(fullGuideRowIndex, selectedTimeline[nextIndex], true);
        flashGuideNavigation(direction);
        return;
      }

      if (direction === "up" || direction === "down") {
        const nextRowIndex = direction === "up"
          ? Math.max(0, fullGuideRowIndex - 1)
          : Math.min(fullGuideRows.length - 1, fullGuideRowIndex + 1);
        if (nextRowIndex === fullGuideRowIndex) return;

        const anchorMs = Number.isFinite(fullGuideTimeAnchorMs)
          ? fullGuideTimeAnchorMs
          : (fullGuideProgrammeMidpointMs(currentCell) ?? epgNowMs);
        selectFullGuideRowAtTime(nextRowIndex, anchorMs);
        flashGuideNavigation(direction);
      }
    };

    const onFullGuideRemoteNav = (e) => {
      const direction = e?.detail?.direction;
      if (["up", "down", "left", "right"].includes(direction)) moveFullGuide(direction);
    };

    const onFullGuideKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase?.() || "";
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "ArrowUp") { e.preventDefault(); moveFullGuide("up"); }
      else if (e.key === "ArrowDown") { e.preventDefault(); moveFullGuide("down"); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); moveFullGuide("left"); }
      else if (e.key === "ArrowRight") { e.preventDefault(); moveFullGuide("right"); }
      else if (e.key === "Enter") {
        if (!selectedFullGuideRow?.id || !selectedFullGuideVideo) return;
        e.preventDefault();
        onSelect?.(selectedFullGuideRow.id, selectedFullGuideIndex);
      }
    };

    window.addEventListener("crt:guide-nav", onFullGuideRemoteNav);
    window.addEventListener("keydown", onFullGuideKey);
    return () => {
      window.removeEventListener("crt:guide-nav", onFullGuideRemoteNav);
      window.removeEventListener("keydown", onFullGuideKey);
    };
  }, [
    searchQ,
    fullGuideRows,
    fullGuideRowIndex,
    fullGuideTimeAnchorMs,
    selectedFullGuideRow?.id,
    selectedFullGuideVideo?.id,
    selectedFullGuideIndex,
    selectedFullGuideProgrammeKey,
    onExtendScheduleWindow,
    onSelect,
  ]);

  // Tune to whatever the highlighted result is. Channels tune to their
  // first tape (index 0); tape hits tune directly to that tape's
  // index within its channel. Closes the Guide via the same `onSelect`
  // path a rail-card click would take.
  const commitSearchHit = (hit) => {
    if (!hit) return;
    if (hit.kind === "channel") {
      onSelect(hit.channel.id, 0);
    } else {
      onSelect(hit.channel.id, hit.index);
    }
    setSearchQuery("");
  };

  // Keyboard nav on the search input: ↑/↓ move highlight, Enter tunes,
  // Escape clears (or falls through to the guide's own Esc-to-close).
  const onSearchKeyDown = (e) => {
    if (!searchResults.flat.length) {
      if (e.key === "Escape") { setSearchQuery(""); e.stopPropagation(); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchIndex((i) => Math.min(searchResults.flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitSearchHit(searchResults.flat[searchIndex]);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setSearchQuery("");
    }
  };

  // Global `/` shortcut hooks into a custom event dispatched from App.js
  // so the App can decide when to open the Guide + focus us in one step
  // without knowing anything about our input's ref. Idempotent — if the
  // Guide is already open we just refocus.
  useEffect(() => {
    const focusSearch = () => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    window.addEventListener("crt:guide-focus-search", focusSearch);
    return () => window.removeEventListener("crt:guide-focus-search", focusSearch);
  }, []);

  // Esc closes + VHS chime on open.
  // Phase 2.3.71-r1.1 - Search owns the first Esc while a query is active.
  // Only an already-clear Search may fall through to the proven 2.3.70
  // immediate Guide close lifecycle.
  useEffect(() => {
    playVhsChime();
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (searchQ.length >= 1) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, searchQ]);

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove("sltv-guide-tools-open");
    };
  }, []);

  return (
    <div
      className={`absolute inset-0 z-40 bg-black/95 p-4 md:p-6 flex flex-col text-[var(--phosphor)] overflow-hidden ${
        layoutMode === "fullscreen" ? "" : "guide-roll-in"
      }`}
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="channel-guide"
    >
      <GuideFirstVisitTour enabled={!isOwner} />
      <div
        data-testid="guide-phone-search"
        className="sltv-guide-phone-search"
        style={{ fontFamily: "VT323, monospace" }}
      >
        <Search size={15} className="shrink-0 opacity-80" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="SEARCH…"
          data-testid="guide-phone-search-input"
          className="min-w-0 w-full bg-transparent border-none outline-none chromatic placeholder:opacity-60"
          aria-label="Search shows or channels"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            data-testid="guide-phone-search-clear"
            className="shrink-0 opacity-70"
            aria-label="Clear phone Guide search"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {layoutMode === "fullscreen" && !guideContentReady && (
        <div
          className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none"
          data-testid="guide-opening-veil"
        >
          <div
            className={`transition-all duration-200 ease-out ${
              guideSurfaceEntered ? "opacity-65 scale-100" : "opacity-0 scale-[0.985]"
            }`}
            style={{ fontFamily: "VT323, monospace" }}
          >
            <div className="text-[10px] tracking-[0.38em] opacity-50 text-center">
              SCARTLEAD.TV GUIDE
            </div>
            <div className="mt-2 h-px w-44 bg-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] overflow-hidden">
              <div className="h-full w-1/2 bg-[var(--phosphor)] opacity-55 animate-pulse" />
            </div>
          </div>
        </div>
      )}
      <div
        className={`flex items-center justify-between border-b border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] pb-2 mb-3 gap-3 ${
          layoutMode === "fullscreen"
            ? `transition-[opacity,transform,filter] duration-200 ease-out ${
                guideContentReady
                  ? "opacity-100 translate-y-0 blur-0"
                  : "opacity-0 -translate-y-1 blur-[1px] pointer-events-none"
              }`
            : ""
        }`}
        data-testid="guide-surface-header"
      >
        {/* Phase 2.3.61f.1 — compact, discoverable Search replaces the redundant Guide title. */}
        <div
          className={`relative flex items-center min-w-0 w-[18rem] md:w-[24rem] border bg-black/55 px-3 py-1.5 transition-all ${
            searchFocused || searchQuery
              ? "border-[var(--phosphor)] phosphor-glow"
              : "border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] animate-pulse"
          }`}
          style={{ fontFamily: "VT323, monospace" }}
          data-testid="guide-search-banner"
        >
          <Search size={14} className="shrink-0 mr-2 opacity-80" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onKeyDown={onSearchKeyDown}
            placeholder="SEARCH SHOWS OR CHANNELS…"
            data-testid="guide-search-input"
            className="w-full min-w-0 bg-transparent border-none outline-none text-[15px] md:text-[17px] tracking-[0.12em] chromatic placeholder:text-[color-mix(in_srgb,var(--phosphor)_58%,transparent)]"
          />
          {searchQuery && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}
              data-testid="guide-search-clear"
              className="shrink-0 ml-2 opacity-60 hover:opacity-100"
              aria-label="Clear search"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {/* Compact welcome pill — the callsign + optional new-tapes
            count on a single header line. Replaces the old prominent
            multi-line welcome bumper that used to live in the rails
            column: viewers already know they just powered the TV on,
            so a one-liner acknowledgement is enough — the freed
            vertical real-estate now belongs to the more actionable
            SEARCH banner beneath. Only renders when the parent hands
            us `welcomeBack` (i.e. this is a returning-viewer session
            with a subtitle / name / new-tapes worth showing). */}
        {welcomeBack && (() => {
          const sub = (welcomeBack.subtitle || "").trim();
          const hasCustomSubtitle = sub && sub.toUpperCase() !== "SIGNAL RESTORED";
          const rightPart = hasCustomSubtitle
            ? sub
            : welcomeBack.name
              ? (welcomeBack.name || "").toUpperCase()
              : "SIGNAL RESTORED";
          return (
            <div
              className="hidden md:flex shrink-0 items-center gap-2 border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] px-3 py-1 tracking-[0.25em] text-[11px] max-w-[24rem] truncate"
              style={{ fontFamily: "VT323, monospace" }}
              data-testid="welcome-back-bumper"
              title={`Welcome back${welcomeBack.name ? ", " + welcomeBack.name : ""}`}
            >
              <span className="opacity-70 shrink-0">
                ▣ {welcomeBack.callsign || "CRT-1986"}
              </span>
              <span className="opacity-40 shrink-0">·</span>
              <span
                className="chromatic phosphor-glow truncate"
                data-testid="welcome-back-subtitle"
              >
                {rightPart}
              </span>
              {welcomeBack.whatsNewCount > 0 && (
                <span
                  className="shrink-0 ml-1 px-1.5 py-0.5 border border-[var(--phosphor)] text-[9px] tracking-[0.3em] phosphor-glow"
                  data-testid="welcome-back-whatsnew"
                >
                  +{welcomeBack.whatsNewCount} NEW
                </span>
              )}
            </div>
          );
        })()}
        <div className="flex items-center gap-2 shrink-0">
          {/* Utility virtual channels — surfaced as compact pills here
              instead of their own MY TV rail. Frees up the entire top
              rail slot that used to sit half-empty behind the CONTACT
              card. Clicking a pill tunes to that virtual channel and
              closes the guide via the same `onSelect` path a rail-card
              click would take. ★ FAVOURITES only renders when the
              viewer has at least one starred tape. */}
          {(channels || []).filter((c) => c.virtual && c.kind === "favourites" && (videosByChannel[c.id] || []).length > 0).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id, 0)}
              data-testid={`guide-header-favourites-pill`}
              title={`Tune to ${c.name}`}
              className="border border-[#FFCC00]/60 text-[#FFCC00] px-3 py-1 tracking-[0.25em] text-[11px] hover:bg-[#FFCC00] hover:text-black transition-colors flex items-center gap-1"
              style={{ fontFamily: "VT323, monospace" }}
            >
              <Star size={12} fill="currentColor" />
              FAVS · {(videosByChannel[c.id] || []).length}
            </button>
          ))}
          <button
            type="button"
            onClick={() => document.documentElement.classList.toggle("sltv-guide-tools-open")}
            data-testid="guide-phone-more"
            className="sltv-guide-phone-more border border-[var(--phosphor)] px-3 py-1 tracking-[0.22em] text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
            style={{ fontFamily: "VT323, monospace" }}
            aria-label="More Guide controls"
          >
            MORE
          </button>
          {(channels || []).filter((c) => c.virtual && c.kind === "contact").map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id, 0)}
              data-testid={`guide-header-contact-pill`}
              title={c.description || `Tune to ${c.name}`}
              className="border border-[var(--phosphor)]/60 text-[var(--phosphor)] px-3 py-1 tracking-[0.25em] text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors flex items-center gap-1"
              style={{ fontFamily: "VT323, monospace" }}
            >
              ✉ {(c.name || "CONTACT").toUpperCase()}
            </button>
          ))}
          {/* Owner-only station-wide layout toggle. Sits next to ESC so
              it's discoverable but never crowds a viewer's chrome
              (hidden entirely for non-owners). Change is universal —
              propagates to every viewer (logged-in + anonymous) via
              `/api/site/layout` on their next 15s poll. */}
          {isOwner && onToggleLayoutMode && (
            <button
              type="button"
              onClick={() => { onToggleLayoutMode(layoutMode === "fullscreen" ? "bezel" : "fullscreen"); setLayoutPulseTick((t) => t + 1); }}
              data-testid="toggle-layout-mode"
              title={
                layoutMode === "fullscreen"
                  ? "Guide currently escapes the CRT bezel (fullscreen — applies to every viewer). Click to return to classic bezel-inside overlays."
                  : "Guide currently renders inside the CRT tube (bezel — applies to every viewer). Click to let it escape to fullscreen for more space."
              }
              className="border border-[#FFAA33]/70 text-[#FFAA33] px-3 py-1 tracking-[0.25em] text-[11px] hover:bg-[#FFAA33] hover:text-black transition-colors flex items-center gap-1"
              style={{ fontFamily: "VT323, monospace" }}
            >
              <span key={`layout-${layoutPulseTick}`} className="inline-block glyph-pulse">
                {layoutMode === "fullscreen" ? "⬜" : "🖼"}
              </span>
              &nbsp;{layoutMode === "fullscreen" ? "FULLSCREEN" : "BEZEL"}
            </button>
          )}
          {guideEdgeFeedback &&
            <div
              data-testid="guide-edge-feedback"
              className="hidden sm:flex items-center border-2 border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_14%,black)] px-2.5 py-1 text-[10px] tracking-[0.16em] phosphor-glow"
              style={{ fontFamily: "VT323, monospace" }}
              role="status"
            >
              {guideEdgeFeedback === "left" ? "← LOADING EARLIER" : "LOADING LATER →"}
            </div>
          }
          {selectedFullGuideDateContext &&
            <div
              data-testid="guide-browse-date-context"
              className="hidden sm:flex items-center border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-black/70 px-2.5 py-1 text-[10px] tracking-[0.18em]"
              style={{ fontFamily: "VT323, monospace" }}
              title="Date of the currently selected programme"
            >
              ◷ {selectedFullGuideDateContext}
            </div>
          }
          {epgExtended && !selectedFullGuideProgrammeContainsNow &&
            <div
              data-testid="guide-extended-range"
              className="hidden sm:flex items-center gap-1 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2.5 py-1 text-[10px] tracking-[0.16em] opacity-80"
              style={{ fontFamily: "VT323, monospace" }}
              title="Extended Guide schedule currently loaded"
            >
              <span className="opacity-65">EXTENDED</span>
              <span>·</span>
              <span>−{formatGuideRangeMinutes(epgLoadedPastMinutes)}</span>
              <span>/</span>
              <span>+{formatGuideRangeMinutes(epgLoadedFutureMinutes)}</span>
            </div>
          }
          {!selectedFullGuideProgrammeContainsNow && selectedFullGuideProgramme &&
            <button
              type="button"
              onClick={returnFullGuideToNow}
              data-testid="guide-back-to-now"
              className="border border-[var(--phosphor)]/70 text-[var(--phosphor)] px-3 py-1 tracking-[0.18em] text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors shrink-0 phosphor-glow"
              style={{ fontFamily: "VT323, monospace" }}
              title="Return this channel to the programme airing now"
            >
              ↺ BACK TO NOW
            </button>
          }
          <button
            onClick={onClose}
            data-testid="close-guide"
            className="text-[var(--phosphor)] border border-[var(--phosphor)] px-3 py-1 text-xs hover:bg-[var(--phosphor)] hover:text-black transition-colors flex items-center gap-1 shrink-0"
          >
            <X size={12} />
            ESC
          </button>
        </div>
      </div>

      {/* Phase 2.3.61e.6.1 — FULL GUIDE is the sole viewer-facing Guide.
          The hidden legacy mode switcher and NOW render tree were retired
          after their unique behaviour was migrated and acceptance-tested. */}
      <div
        className={`flex-1 min-h-0 grid grid-rows-[minmax(0,1fr)_auto] gap-3 ${
          searchQ.length >= 1
            ? "hidden"
            : layoutMode === "fullscreen"
              ? `transition-[opacity,transform,filter] duration-200 ease-out ${
                  guideContentReady
                    ? "opacity-100 translate-y-0 blur-0"
                    : "opacity-0 translate-y-1 blur-[1px] pointer-events-none"
                }`
              : ""
        }`}
        data-testid="guide-full-timeline"
        data-guide-media-host-persistent="true"
      >
          <div ref={fullGuideListRef} className="min-h-0 overflow-auto scrollbar-crt border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/35" data-testid="guide-full-list">
            {/* Phase 2.3.62.4-r3 — real horizontal clock EPG. */}
            <div
              className="sticky top-0 z-30 flex min-w-max bg-black border-b-2 border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] text-[9px] tracking-[0.2em] shadow-[0_6px_18px_rgba(0,0,0,0.55)]"
              data-testid="guide-full-header"
            >
              <div
                className="sticky left-0 z-40 shrink-0 px-3 py-2 border-r-2 border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] bg-black text-[var(--phosphor)]"
                style={{ width: `${EPG_CHANNEL_WIDTH_PX}px` }}
              >
                CHANNEL
              </div>
              <div
                className="relative shrink-0 h-11 bg-black/95 pt-2"
                style={{ width: `${epgCanvasWidthPx}px` }}
                data-testid="guide-clock-ruler"
              >
                {epgClockTicks.map((tick) => (
                  <div
                    key={tick.ms}
                    className="absolute top-0 bottom-0 border-l border-[color-mix(in_srgb,var(--phosphor)_32%,transparent)] px-2 pt-2 tabular-nums"
                    style={{ left: `${tick.leftPx}px` }}
                  >
                    {tick.label}
                    {tick.dayBoundary &&
                      <span
                        className="absolute top-0 left-0 -translate-y-full whitespace-nowrap border border-[var(--phosphor)] bg-black px-1.5 py-0.5 text-[7px] tracking-[0.14em] phosphor-glow"
                        data-testid={`guide-day-boundary-${tick.ms}`}
                      >
                        {tick.dayLabel}
                      </span>
                    }
                  </div>
                ))}
                {epgWindowValid && (
                  <div
                    className="absolute top-0 bottom-0 z-20 w-[3px] -translate-x-1/2 bg-[var(--phosphor)] shadow-[0_0_4px_var(--phosphor),0_0_10px_var(--phosphor),0_0_18px_color-mix(in_srgb,var(--phosphor)_55%,transparent)]"
                    style={{ left: `${epgNowLeftPx}px` }}
                    data-testid="guide-now-line-header"
                  >
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 border-2 border-[var(--phosphor)] bg-black text-[var(--phosphor)] px-2 py-0.5 text-[9px] font-black tracking-[0.18em] shadow-[0_0_8px_color-mix(in_srgb,var(--phosphor)_70%,transparent)] phosphor-glow">
                      NOW
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-max">
              {fullGuideRows.map((row, rowIndex) => {
                const rowSelected = rowIndex === fullGuideRowIndex;
                const timeline = fullGuideTimelineFor(row);
                return (
                  <div
                    key={row.id}
                    ref={(node) => {
                      if (node) fullGuideRowRefs.current.set(row.id, node);
                      else fullGuideRowRefs.current.delete(row.id);
                    }}
                    className={`relative flex border-b-2 transition-all ${
                      rowSelected
                        ? "z-10 border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)] shadow-[inset_0_0_0_2px_color-mix(in_srgb,var(--phosphor)_70%,transparent),inset_0_0_28px_color-mix(in_srgb,var(--phosphor)_9%,transparent)]"
                        : row.isCurrent
                          ? "border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)]"
                    }`}
                    data-testid={`guide-full-row-${row.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const anchorMs = Number.isFinite(fullGuideTimeAnchorMs)
                          ? fullGuideTimeAnchorMs
                          : epgNowMs;
                        selectFullGuideRowAtTime(rowIndex, anchorMs);
                      }}
                      className={`sticky left-0 z-30 shrink-0 text-left px-2.5 py-2.5 border-r-2 bg-[rgba(0,0,0,0.99)] transition-all ${
                        rowSelected
                          ? "border-[var(--phosphor)] shadow-[inset_4px_0_0_var(--phosphor),10px_0_22px_rgba(0,0,0,0.55)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] shadow-[8px_0_18px_rgba(0,0,0,0.38)]"
                      }`}
                      style={{ width: `${EPG_CHANNEL_WIDTH_PX}px` }}
                    >
                      <div
                        className="relative min-h-[5.8rem] w-full overflow-hidden"
                        data-testid={`guide-channel-identity-${row.id}`}
                      >
                        <div className="absolute inset-x-1.5 top-1.5 bottom-6 flex items-center justify-center">
                          {fullGuideChannelLogoFor(row) ? (
                            <div
                              className={`w-full h-full flex items-center justify-center overflow-hidden transition-all ${
                                rowSelected
                                  ? "opacity-100"
                                  : row.isCurrent
                                    ? "opacity-92"
                                    : "opacity-78"
                              }`}
                              data-testid={`guide-channel-logo-${row.id}`}
                            >
                              <img
                                src={fullGuideChannelLogoFor(row)}
                                alt={row.name || ""}
                                draggable="false"
                                className="max-w-full max-h-full w-full h-full object-contain px-2 py-1"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              className={`max-w-full text-center leading-tight tracking-[0.08em] ${
                                rowSelected
                                  ? "text-[15px] chromatic"
                                  : "text-[13px] opacity-78"
                              }`}
                              style={{ fontFamily: "VT323, monospace" }}
                              data-testid={`guide-channel-name-fallback-${row.id}`}
                            >
                              {row.name}
                            </div>
                          )}
                        </div>

                        {rowSelected && (
                          <div
                            className="absolute top-0 right-0 text-[6px] tracking-[0.12em] border border-[var(--phosphor)] bg-[var(--phosphor)] text-black px-1.5 py-0.5 font-bold shadow-[0_0_9px_var(--phosphor)]"
                            data-testid="guide-selected-channel-badge"
                          >
                            ▶ SELECTED
                          </div>
                        )}

                        <div
                          className={`absolute left-0 bottom-0 tabular-nums tracking-[0.12em] ${
                            rowSelected ? "text-[11px] chromatic" : "text-[10px] opacity-72"
                          }`}
                          data-testid={`guide-channel-number-${row.id}`}
                        >
                          CH {row.numberLabel || String(row.number ?? "").padStart(2, "0")}
                        </div>

                        {row.isCurrent && (
                          <div
                            className="absolute right-0 bottom-0 text-[7px] tracking-[0.16em] border border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,black)] px-1.5 py-0.5 phosphor-glow"
                            data-testid={`guide-channel-live-${row.id}`}
                          >
                            ● LIVE
                          </div>
                        )}
                      </div>
                    </button>

                    <div
                      className="relative shrink-0 min-h-[6.8rem] bg-black/20"
                      style={{ width: `${epgCanvasWidthPx}px` }}
                      data-testid={`guide-time-canvas-${row.id}`}
                    >
                      {epgClockTicks.map((tick) => (
                        <div
                          key={`grid-${tick.ms}`}
                          className="absolute top-0 bottom-0 border-l border-[color-mix(in_srgb,var(--phosphor)_10%,transparent)] pointer-events-none"
                          style={{ left: `${tick.leftPx}px` }}
                        />
                      ))}

                      {epgWindowValid && (
                        <div
                          className="absolute top-0 bottom-0 z-20 w-[3px] -translate-x-1/2 bg-[var(--phosphor)] pointer-events-none shadow-[0_0_4px_var(--phosphor),0_0_9px_var(--phosphor),0_0_16px_color-mix(in_srgb,var(--phosphor)_50%,transparent)]"
                          style={{ left: `${epgNowLeftPx}px` }}
                          data-testid={`guide-now-line-${row.id}`}
                        />
                      )}

                      {timeline.map((cell) => {
                        const cellKey = fullGuideProgrammeKey(row, cell);
                        const selected =
                          rowSelected && cellKey === selectedFullGuideProgrammeKey;
                        const isNow = !!cell.is_live_now;
                        const parts = fullGuideProgrammeParts(cell.video);
                        const art =
                          cell.video?.backdrop_url ||
                          cell.video?.poster_url ||
                          row.channel?.thumbnail_url ||
                          row.channel?.guide_poster_url ||
                          null;

                        return (
                          <button
                            key={`${row.id}-${cell.programme_start}-${cell.index}`}
                            ref={(node) => {
                              if (node && cellKey) fullGuideCellRefs.current.set(cellKey, node);
                              else if (cellKey) fullGuideCellRefs.current.delete(cellKey);
                            }}
                            type="button"
                            onClick={() => {
                              selectFullGuideProgramme(rowIndex, cell, true);
                            }}
                            onDoubleClick={() => onSelect?.(row.id, cell.index)}
                            className={`absolute top-0 bottom-0 text-left overflow-hidden transition-all duration-150 border-r border-l border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] ${
                              isNow && !selected
                                ? "bg-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] shadow-[inset_0_0_0_2px_color-mix(in_srgb,var(--phosphor)_72%,transparent),inset_0_0_20px_color-mix(in_srgb,var(--phosphor)_16%,transparent)]"
                                : "bg-black/30"
                            } ${
                              selected
                                ? `z-30 opacity-100 outline outline-[6px] outline-[var(--phosphor)] outline-offset-[-6px] bg-[color-mix(in_srgb,var(--phosphor)_46%,transparent)] brightness-125 shadow-[inset_0_0_0_3px_black,inset_0_0_0_5px_color-mix(in_srgb,var(--phosphor)_88%,transparent),inset_0_0_38px_color-mix(in_srgb,var(--phosphor)_34%,transparent),0_0_12px_var(--phosphor),0_0_34px_color-mix(in_srgb,var(--phosphor)_78%,transparent)] scale-[1.022] ${guideNavPulse ? "brightness-150" : ""}`
                                : "opacity-82 hover:opacity-100 hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)]"
                            }`}
                            style={{
                              left: `${cell.leftPx}px`,
                              width: `${cell.widthPx}px`,
                            }}
                            data-testid={`guide-full-cell-${row.id}-${cell.index}`}
                          >
                            {art && (
                              <img
                                src={art}
                                alt=""
                                draggable="false"
                                className={`absolute inset-0 w-full h-full object-cover transition-opacity ${
                                  selected ? "opacity-68 saturate-125" : "opacity-22"
                                }`}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            )}
                            <div
                              className={`absolute inset-0 pointer-events-none ${
                                selected
                                  ? "bg-gradient-to-t from-black/88 via-black/32 to-transparent"
                                  : "bg-gradient-to-t from-black/95 via-black/72 to-black/42"
                              }`}
                            />
                            <div className="relative z-10 h-full p-2.5 flex flex-col justify-between">
                              <div className="flex items-start justify-end gap-2">
                                {isNow && !selected && cell.widthPx >= 88 && (
                                  <span className="shrink-0 text-[7px] font-bold tracking-[0.16em] border border-[var(--phosphor)] bg-black/85 text-[var(--phosphor)] px-1.5 py-0.5 phosphor-glow">
                                    ● ON NOW
                                  </span>
                                )}
                                {selected && cell.widthPx >= 78 &&
                                  <span
                                    className="shrink-0 text-[7px] tracking-[0.16em] border-2 border-[var(--phosphor)] bg-[var(--phosphor)] text-black px-1.5 py-0.5 font-bold shadow-[0_0_12px_var(--phosphor)]"
                                    data-testid="guide-selected-programme-badge"
                                  >
                                    ◆ SELECTED
                                  </span>
                                }
                              </div>

                              <div className="min-w-0">
                                <div className={`leading-[1.08] line-clamp-2 font-semibold drop-shadow-[0_2px_3px_rgba(0,0,0,0.95)] ${
                                  selected
                                    ? "text-[16px] md:text-[18px] chromatic"
                                    : "text-[14px] md:text-[16px]"
                                }`}>
                                  {parts.title}
                                </div>
                                <div className={`mt-1.5 tabular-nums whitespace-nowrap font-medium tracking-[0.08em] drop-shadow-[0_1px_2px_rgba(0,0,0,0.95)] ${
                                  selected
                                    ? "text-[10px] md:text-[11px] opacity-95"
                                    : "text-[9px] md:text-[10px] opacity-82"
                                }`}>
                                  {fullGuideProgrammeSlotLabel(cell)}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Phase 2.3.61d.3 — rich programme information dock.
              The lower guide now uses its full width deliberately: preview,
              readable synopsis/details, and optional poster art. */}
          <div
            className={`shrink-0 grid gap-3 border-2 border-[color-mix(in_srgb,var(--phosphor)_34%,transparent)] bg-black/88 p-3 ${
              fullGuideOperatorOpen
                ? "xl:grid-cols-[minmax(14rem,20rem)_minmax(0,1fr)]"
                : fullGuideFeaturedOpen
                  ? "xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start"
                  : "xl:grid-cols-[minmax(0,1fr)_18rem_minmax(18rem,24rem)] xl:items-start"
            }`}
            data-testid="guide-full-selection"
          >
            <div
              className={`relative w-full aspect-video border-2 border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] bg-black overflow-hidden self-center ${
                !fullGuideOperatorOpen ? "xl:col-start-2 xl:row-start-1" : ""
              }`}
              data-testid="guide-full-live-preview"
            >
              <div
                ref={fullGuideLiveHostRef}
                className="absolute inset-0 z-10 overflow-hidden"
                data-testid="guide-full-live-host"
              />
              <div className="absolute top-2 left-2 z-20 pointer-events-none border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/80 px-2 py-1 text-[8px] tracking-[0.2em]">
                ● LIVE TV · CONTINUOUS
              </div>
              <div className="absolute inset-0 pointer-events-none scanlines z-20" />
            </div>

            <div className={`min-w-0 grid grid-rows-[auto_auto_minmax(0,1fr)_auto] pr-1 ${
                !fullGuideOperatorOpen ? "xl:col-start-3 xl:row-start-1" : ""
              } ${
                !fullGuideOperatorOpen && fullGuideFeaturedOpen ? "hidden" : ""
              }`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[9px] tracking-[0.18em] opacity-60">
                  {selectedFullGuideRow
                    ? `CH ${selectedFullGuideRow.numberLabel || String(selectedFullGuideRow.number ?? "").padStart(2, "0")} · ${selectedFullGuideRow.name}`
                    : "NO CHANNEL SELECTED"}
                </div>
                <span className="text-[8px] tracking-[0.16em] border border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] px-1.5 py-0.5">
                  {selectedFullGuideFeaturedEntry
                    ? "★ FEATURED"
                    : (() => {
                        const block = selectedFullGuideRow?.programmeBlocks?.find(
                          (item) => item?.video_index === selectedFullGuideIndex
                        );
                        if (!block) return fullGuideOffset === 0 ? "● NOW" : "PROGRAMME";
                        if (block.is_live_now) return "● NOW";
                        const start = new Date(block.programme_start || "");
                        return Number.isNaN(start.getTime())
                          ? "PROGRAMME"
                          : start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                      })()}
                </span>
              </div>

              <div className="mt-1">
                {(() => {
                  if (selectedFullGuideFeaturedEntry) {
                    return (
                      <>
                        <div className="text-[9px] tracking-[0.18em] text-[var(--phosphor)] opacity-78 mb-1">
                          ★ FEATURED PICK
                        </div>
                        <div className="text-lg md:text-xl chromatic leading-tight line-clamp-2">
                          {selectedFullGuideFeaturedEntry.title ||
                            selectedFullGuideRow?.name ||
                            "FEATURED"}
                        </div>
                      </>
                    );
                  }

                  const parts = fullGuideProgrammeParts(selectedFullGuideVideo);
                  const rawTitle = String(selectedFullGuideVideo?.title || "");
                  const titleEpisodeMatch = rawTitle.match(/\b(S\d{1,2}E\d{1,3})\b/i);
                  const episodeCode =
                    selectedFullGuideVideo?.season_number != null && selectedFullGuideVideo?.episode_number != null
                      ? `S${String(selectedFullGuideVideo.season_number).padStart(2, "0")}E${String(selectedFullGuideVideo.episode_number).padStart(2, "0")}`
                      : (titleEpisodeMatch ? titleEpisodeMatch[1].toUpperCase() : null);

                  const meta = [
                    episodeCode,
                    selectedFullGuideVideo?.year || selectedFullGuideVideo?.release_year,
                    selectedFullGuideVideo?.runtime ? `${selectedFullGuideVideo.runtime} MIN` : null,
                    selectedFullGuideVideo?.genre,
                    selectedFullGuideVideo?.rating || selectedFullGuideVideo?.certification,
                  ].filter(Boolean);

                  return (
                    <>
                      {episodeCode && (
                        <div className="text-[9px] tracking-[0.16em] opacity-62 mb-0.5">{episodeCode}</div>
                      )}
                      <div className="text-lg md:text-xl chromatic leading-tight line-clamp-2">
                        {parts.title}
                      </div>
                      {meta.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5 text-[8px] tracking-[0.12em]">
                          {meta.map((item, idx) => (
                            <span
                              key={`${item}-${idx}`}
                              className="border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_4%,transparent)] px-1.5 py-0.5 opacity-62"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {!selectedFullGuideFeaturedEntry && (
                <div className="mt-2 min-h-0 max-w-[76ch]">
                  <div className="text-[8px] tracking-[0.18em] opacity-45 mb-1">
                    PROGRAMME SYNOPSIS
                  </div>
                  <p
                    className="text-[10px] md:text-[11px] leading-[1.45] opacity-82 line-clamp-2"
                    data-testid="guide-full-synopsis"
                  >
                    {(selectedFullGuideVideo?.description ||
                      selectedFullGuideVideo?.overview ||
                      selectedFullGuideVideo?.synopsis ||
                      selectedFullGuideVideo?.summary ||
                      selectedFullGuideRow?.channel?.description ||
                      "No programme synopsis is available yet.").trim()}
                  </p>
                </div>
              )}

              <div className="mt-2 flex items-center justify-between gap-3 border-t border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)] pt-2">
                <div className="flex flex-wrap items-center gap-2 text-[8px] tracking-[0.14em]">
                  {selectedFullGuideRow?.isCurrent && fullGuideOffset === 0 && (
                    <span className="border border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] px-1.5 py-0.5 opacity-72">
                      ● ON AIR
                    </span>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {isOwner && fullGuideOperatorChannel && (
                    <button
                      type="button"
                      onClick={() => {
                        setFullGuideOperatorOpen((open) => !open);
                      }}
                      className={`border px-3 py-1.5 text-[8px] tracking-[0.16em] transition-colors ${
                        fullGuideOperatorOpen
                          ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_14%,transparent)] text-[var(--phosphor)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] hover:border-[var(--phosphor)]"
                      }`}
                      data-testid="guide-full-operator-toggle"
                      aria-expanded={fullGuideOperatorOpen}
                    >
                      {fullGuideOperatorOpen ? "▲ HIDE CHANNEL" : "⚙ CHANNEL"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!selectedFullGuideRow?.id || !selectedFullGuideVideo}
                    onClick={() => {
                      const selectedIsLive = !!selectedFullGuideProgramme?.is_live_now;
                      const selectedAlreadyPlaying =
                        selectedIsLive &&
                        selectedFullGuideRow?.id === currentChannelId &&
                        selectedFullGuideVideo?.id === currentVideo?.id;

                      if (selectedAlreadyPlaying) {
                        onClose?.();
                      } else if (selectedIsLive) {
                        onSelect?.(selectedFullGuideRow.id);
                      } else {
                        onSelect?.(selectedFullGuideRow.id, selectedFullGuideIndex);
                      }
                    }}
                    className="border-2 border-[var(--phosphor)] px-3.5 py-1.5 text-[9px] tracking-[0.18em] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-35"
                    data-testid="guide-full-watch"
                  >
                    ▶ WATCH NOW
                  </button>
                </div>
              </div>
            </div>

            {(() => {
              const poster =
                selectedFullGuideVideo?.poster_url ||
                selectedFullGuideRow?.channel?.guide_poster_url ||
                selectedFullGuideRow?.channel?.thumbnail_url ||
                null;

              if (!poster) return null;

              return (
                <div
                  className="hidden"
                  data-testid="guide-full-poster"
                >
                  <img
                    src={poster}
                    alt=""
                    draggable="false"
                    className="w-full h-full object-cover opacity-88"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                  />
                </div>
              );
            })()}

            {isOwner && fullGuideOperatorOpen && fullGuideOperatorChannel && (
              <div
                className="xl:col-span-full min-w-0 border-t border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] pt-2"
                data-testid="guide-full-operator"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <div className="text-[9px] tracking-[0.2em] opacity-74">
                      ┌─ ⚙ CHANNEL OPERATOR · {selectedFullGuideRow?.name || fullGuideOperatorChannel.name || "CHANNEL"} ─┐
                    </div>
                    <div className="text-[8px] tracking-[0.14em] opacity-42 mt-0.5">
                      OWNER ONLY · CHANGES APPLY TO THE HIGHLIGHTED FULL GUIDE CHANNEL
                    </div>
                  </div>
                  <div className="text-[8px] tracking-[0.14em] opacity-48">
                    {fullGuideOperatorChannel.isSubChannel ? "SUB-CHANNEL" : "CHANNEL"}
                  </div>
                </div>

                <input
                  ref={fullGuideOperatorArtInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="guide-full-operator-art-input"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = "";
                    const channel = fullGuideOperatorChannel;
                    if (!file || !channel || channel.isSubChannel) return;

                    setFullGuideOperatorUploading(true);
                    try {
                      const resized = await resizeImageForUpload(file);
                      const fd = new FormData();
                      fd.append("file", resized);
                      await axios.post(
                        `${apiBase}/channels/${channel.id}/thumbnail`,
                        fd,
                        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
                      );
                      toast.success("CHANNEL ART UPDATED");
                      if (onChannelArtChanged) await onChannelArtChanged();
                    } catch (err) {
                      toast.error("UPLOAD FAILED", {
                        description: err.response?.data?.detail || err.message,
                      });
                    } finally {
                      setFullGuideOperatorUploading(false);
                    }
                  }}
                />

                <input
                  ref={fullGuideOperatorLogoInputRef}
                  type="file"
                  accept="image/png,image/svg+xml,image/webp,image/jpeg"
                  className="hidden"
                  data-testid="guide-full-operator-logo-input-file"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = "";
                    const channel = fullGuideOperatorChannel;
                    if (!file || !channel || channel.isSubChannel) return;

                    setFullGuideOperatorUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", file);
                      await axios.post(
                        `${apiBase}/channels/${channel.id}/logo`,
                        fd,
                        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
                      );
                      toast.success("STATION LOGO UPDATED");
                      if (onChannelArtChanged) await onChannelArtChanged();
                    } catch (err) {
                      toast.error("LOGO UPLOAD FAILED", {
                        description: err.response?.data?.detail || err.message,
                      });
                    } finally {
                      setFullGuideOperatorUploading(false);
                    }
                  }}
                />

                <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-2">
                  <section className="border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/45 p-2.5">
                    <div className="text-[8px] tracking-[0.18em] opacity-70 mb-2">CHANNEL ART</div>
                    <div className="flex items-center gap-2">
                      <input
                        type="url"
                        value={fullGuideOperatorDraft.thumbnail}
                        onChange={(e) => setFullGuideOperatorDraft((draft) => ({ ...draft, thumbnail: e.target.value }))}
                        placeholder="https://… channel thumbnail"
                        className="flex-1 min-w-0 bg-black border border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] px-2 py-1.5 text-[9px] outline-none"
                        data-testid="guide-full-operator-thumbnail-input"
                      />
                      <button
                        type="button"
                        onClick={() => saveFullGuideOperatorField("thumbnail")}
                        disabled={!!fullGuideOperatorSaving}
                        className="border border-[var(--phosphor)] px-2 py-1.5 text-[8px] tracking-[0.13em] disabled:opacity-35"
                        data-testid="guide-full-operator-thumbnail-save"
                      >
                        {fullGuideOperatorSaving === "thumbnail" ? "…" : "SAVE"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {!fullGuideOperatorChannel.isSubChannel && (
                        <button
                          type="button"
                          onClick={() => fullGuideOperatorArtInputRef.current?.click()}
                          disabled={fullGuideOperatorUploading}
                          className="border border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] px-2 py-1 text-[8px] tracking-[0.13em] hover:border-[var(--phosphor)] disabled:opacity-35"
                          data-testid="guide-full-operator-art-upload"
                        >
                          {fullGuideOperatorUploading ? "UPLOADING…" : "UPLOAD JPG/PNG"}
                        </button>
                      )}
                      {!fullGuideOperatorChannel.isSubChannel && (fullGuideOperatorChannel.thumbnail_path || fullGuideOperatorChannel.thumbnail_url) && (
                        <button
                          type="button"
                          onClick={async () => {
                            setFullGuideOperatorUploading(true);
                            try {
                              await axios.delete(
                                `${apiBase}/channels/${fullGuideOperatorChannel.id}/thumbnail`,
                                { headers: authHeaders() }
                              );
                              toast.success("CHANNEL ART CLEARED");
                              setFullGuideOperatorDraft((draft) => ({ ...draft, thumbnail: "" }));
                              if (onChannelArtChanged) await onChannelArtChanged();
                            } catch (err) {
                              toast.error("CLEAR FAILED", {
                                description: err.response?.data?.detail || err.message,
                              });
                            } finally {
                              setFullGuideOperatorUploading(false);
                            }
                          }}
                          disabled={fullGuideOperatorUploading}
                          className="border border-[#FF3B30]/45 text-[#FF3B30] px-2 py-1 text-[8px] tracking-[0.13em] disabled:opacity-35"
                          data-testid="guide-full-operator-art-clear"
                        >
                          CLEAR
                        </button>
                      )}
                    </div>
                  </section>

                  <section className="border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/45 p-2.5">
                    <div className="text-[8px] tracking-[0.18em] opacity-70 mb-2">STATION LOGO</div>
                    {fullGuideOperatorChannel.isSubChannel ? (
                      <div className="text-[8px] border border-[color-mix(in_srgb,var(--phosphor)_26%,transparent)] px-2 py-2 opacity-58">
                        STATION LOGO IS MANAGED ON THE PARENT CHANNEL.
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            value={fullGuideOperatorDraft.logo}
                            onChange={(e) => setFullGuideOperatorDraft((draft) => ({ ...draft, logo: e.target.value }))}
                            placeholder="https://… transparent station logo"
                            className="flex-1 min-w-0 bg-black border border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] px-2 py-1.5 text-[9px] outline-none"
                            data-testid="guide-full-operator-logo-input"
                          />
                          <button
                            type="button"
                            onClick={() => saveFullGuideOperatorField("logo")}
                            disabled={!!fullGuideOperatorSaving}
                            className="border border-[var(--phosphor)] px-2 py-1.5 text-[8px] tracking-[0.13em] disabled:opacity-35"
                            data-testid="guide-full-operator-logo-save"
                          >
                            {fullGuideOperatorSaving === "logo" ? "…" : "SAVE"}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => fullGuideOperatorLogoInputRef.current?.click()}
                            disabled={fullGuideOperatorUploading}
                            className="border border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] px-2 py-1 text-[8px] tracking-[0.13em] hover:border-[var(--phosphor)] disabled:opacity-35"
                            data-testid="guide-full-operator-logo-upload"
                          >
                            {fullGuideOperatorUploading ? "UPLOADING…" : "UPLOAD LOGO"}
                          </button>
                          {(fullGuideOperatorChannel.logo_path || fullGuideOperatorChannel.logo_url) && (
                            <button
                              type="button"
                              onClick={async () => {
                                setFullGuideOperatorUploading(true);
                                try {
                                  await axios.delete(
                                    `${apiBase}/channels/${fullGuideOperatorChannel.id}/logo`,
                                    { headers: authHeaders() }
                                  );
                                  toast.success("STATION LOGO CLEARED");
                                  setFullGuideOperatorDraft((draft) => ({ ...draft, logo: "" }));
                                  if (onChannelArtChanged) await onChannelArtChanged();
                                } catch (err) {
                                  toast.error("LOGO CLEAR FAILED", {
                                    description: err.response?.data?.detail || err.message,
                                  });
                                } finally {
                                  setFullGuideOperatorUploading(false);
                                }
                              }}
                              disabled={fullGuideOperatorUploading}
                              className="border border-[#FF3B30]/45 text-[#FF3B30] px-2 py-1 text-[8px] tracking-[0.13em] disabled:opacity-35"
                              data-testid="guide-full-operator-logo-clear"
                            >
                              CLEAR
                            </button>
                          )}
                        </div>
                        <div className="mt-2 text-[7px] tracking-[0.1em] opacity-42">
                          TRANSPARENT PNG/SVG/WEBP RECOMMENDED · USED AS THE ON-AIR CHANNEL BUG
                        </div>
                      </>
                    )}
                  </section>

                  <section className="border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/45 p-2.5">
                    <div className="text-[8px] tracking-[0.18em] opacity-70 mb-2">GUIDE POSTER</div>
                    <div className="flex items-center gap-2">
                      <input
                        type="url"
                        value={fullGuideOperatorDraft.poster}
                        onChange={(e) => setFullGuideOperatorDraft((draft) => ({ ...draft, poster: e.target.value }))}
                        placeholder="https://… preview/poster artwork"
                        className="flex-1 min-w-0 bg-black border border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] px-2 py-1.5 text-[9px] outline-none"
                        data-testid="guide-full-operator-poster-input"
                      />
                      <button
                        type="button"
                        onClick={() => saveFullGuideOperatorField("poster")}
                        disabled={!!fullGuideOperatorSaving}
                        className="border border-[var(--phosphor)] px-2 py-1.5 text-[8px] tracking-[0.13em] disabled:opacity-35"
                        data-testid="guide-full-operator-poster-save"
                      >
                        {fullGuideOperatorSaving === "poster" ? "…" : "SAVE"}
                      </button>
                    </div>
                    <div className="mt-2 text-[7px] tracking-[0.1em] opacity-42">
                      {fullGuideOperatorChannel.isSubChannel
                        ? "SUB-CHANNEL ART USES ITS PARENT SHOW-ART SLOT"
                        : "USED BY GUIDE/PREVIEW ARTWORK FALLBACKS"}
                    </div>
                  </section>

                  <section className="border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/45 p-2.5 flex flex-col">
                    <div className="text-[8px] tracking-[0.18em] opacity-70 mb-2">TMDB ARTWORK</div>
                    <div className="text-[8px] leading-relaxed opacity-58 flex-1">
                      Search TMDb once, verify the exact movie or TV identity, then choose dedicated artwork for:
                      <div className="mt-2 grid grid-cols-1 gap-1 tracking-[0.11em]">
                        <span>• CHANNEL THUMBNAIL · POSTER</span>
                        <span>• STATION LOGO · TRANSPARENT LOGO</span>
                        <span>• GUIDE POSTER · BACKDROP</span>
                      </div>
                    </div>
                    {fullGuideOperatorChannel.isSubChannel ? (
                      <div className="mt-3 text-[8px] border border-[color-mix(in_srgb,var(--phosphor)_26%,transparent)] px-2 py-1.5 opacity-58">
                        SUB-CHANNELS USE PARENT SHOW ART. TMDb CHANNEL ARTWORK IS AVAILABLE ON REAL CHANNELS.
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setFullGuideTmdbArtworkOpen(true)}
                        className="mt-3 border border-[var(--phosphor)] px-3 py-1.5 text-[8px] tracking-[0.14em] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
                        data-testid="guide-full-operator-tmdb-open"
                      >
                        🎬 FIND / REFRESH FROM TMDB
                      </button>
                    )}
                    <div className="mt-2 text-[7px] tracking-[0.1em] opacity-40">
                      NOTHING IS OVERWRITTEN UNTIL YOU APPLY THE SELECTED ARTWORK.
                    </div>
                  </section>
                </div>
              </div>
            )}

            {!fullGuideOperatorOpen && fullGuideFeaturedOpen && featuredEntries.length > 0 && (
              <div
                className="xl:col-start-1 xl:row-start-1 min-w-0 border-t border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] pt-2"
                data-testid="guide-smart-discovery-strip"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFullGuideDiscoveryMode("featured")}
                      className="border border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_14%,transparent)] text-[var(--phosphor)] px-3 py-1 text-[9px] tracking-[0.18em] phosphor-glow"
                      data-testid="guide-full-featured-toggle"
                      aria-pressed="true"
                    >
                      ★ FEATURED
                    </button>
                    <button
                      type="button"
                      disabled={!selectedFullGuideRow?.playlist?.length}
                      onClick={() => setFullGuideDiscoveryMode("programmes")}
                      className="border border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] px-3 py-1 text-[9px] tracking-[0.18em] hover:border-[var(--phosphor)] disabled:opacity-35"
                      data-testid="guide-full-programmes-toggle"
                      aria-pressed="false"
                    >
                      ▦ PROGRAMMES
                    </button>
                    <span className="hidden md:inline text-[8px] tracking-[0.12em] opacity-42">
                      PICKS FOR YOU · SELECT TO BROWSE
                    </span>
                  </div>
                  <div className="text-[8px] tracking-[0.14em] opacity-48">
                    {featuredEntries.length} PICKS
                  </div>
                </div>

                <div className="flex gap-2 overflow-x-auto scrollbar-crt pb-1" data-testid="guide-full-featured">
                  {featuredEntries.map((entry, index) => {
                    const targetRow = fullGuideRows.find((row) => row?.id === entry?.target_channel_id) || null;
                    const targetPlaylist = Array.isArray(targetRow?.playlist) ? targetRow.playlist : [];
                    const targetVideoIndex =
                      entry?.target_video_id
                        ? targetPlaylist.findIndex((video) => video?.id === entry.target_video_id)
                        : 0;
                    const targetVideo =
                      targetVideoIndex >= 0
                        ? targetPlaylist[targetVideoIndex] || null
                        : targetPlaylist[0] || null;
                    const canBrowse = !!targetRow;
                    const selected =
                      canBrowse &&
                      selectedFullGuideRow?.id === targetRow.id &&
                      (
                        entry?.target_video_id
                          ? selectedFullGuideVideo?.id === entry.target_video_id
                          : selectedFullGuideIndex === Math.max(0, targetVideoIndex)
                      );
                    const art =
                      entry?.backdrop_url ||
                      targetVideo?.backdrop_url ||
                      entry?.poster_url ||
                      targetVideo?.poster_url ||
                      targetRow?.channel?.thumbnail_url ||
                      targetRow?.channel?.guide_poster_url ||
                      null;

                    return (
                      <div
                        key={entry?.id || `featured-${index}`}
                        className="relative shrink-0 w-[18rem] xl:w-auto xl:min-w-[12rem] xl:flex-1 h-[8rem]"
                      >
                        <button
                          type="button"
                          disabled={!canBrowse}
                          onClick={() => {
                            if (!canBrowse) return;
                            selectFullGuideTarget(
                              targetRow.id,
                              entry?.target_video_id || null,
                              targetVideoIndex >= 0 ? targetVideoIndex : 0,
                            );
                          }}
                          className={`relative w-full h-full overflow-hidden border text-left transition-all disabled:opacity-45 ${
                            selected
                              ? "border-2 border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] shadow-[inset_0_0_0_2px_color-mix(in_srgb,var(--phosphor)_52%,transparent),0_0_16px_color-mix(in_srgb,var(--phosphor)_46%,transparent)]"
                              : "border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/55 hover:border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)]"
                          }`}
                          data-testid={`guide-full-featured-${index}`}
                          aria-current={selected ? "true" : undefined}
                          title={canBrowse ? `Browse ${entry?.title || targetRow?.name || "featured programme"}` : "Featured target is not available in FULL GUIDE"}
                        >
                        {art && (
                          <img
                            src={art}
                            alt=""
                            draggable="false"
                            className={`absolute inset-0 w-full h-full object-cover ${selected ? "opacity-48" : "opacity-30"}`}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/68 to-black/22" />
                        <div className="relative z-10 h-full p-2.5 flex flex-col justify-between">
                          <div className="flex items-start justify-between gap-2 text-[7px] tracking-[0.13em]">
                            <span className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-black/65 px-1.5 py-0.5">
                              ★ FEATURED
                            </span>
                            {targetRow && (
                              <span className="opacity-65 truncate">
                                CH {targetRow.numberLabel || String(targetRow.number ?? "").padStart(2, "0")}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className={`leading-tight line-clamp-2 font-semibold ${selected ? "text-[16px] chromatic" : "text-[15px]"}`}>
                              {entry?.title || targetVideo?.title || targetRow?.name || "FEATURED"}
                            </div>
                            {entry?.tagline && (
                              <div className="mt-1.5 text-[10px] leading-tight opacity-70 line-clamp-1">
                                {entry.tagline}
                              </div>
                            )}
                            {selected && (
                              <div className="mt-1 text-[7px] tracking-[0.16em] opacity-80">▶ SELECTED</div>
                            )}
                          </div>
                        </div>
                        </button>

                        {selected && canBrowse && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();

                              const targetIndex =
                                targetVideoIndex >= 0 ? targetVideoIndex : 0;

                              const targetProgramme =
                                targetRow?.programmeBlocks?.find(
                                  (item) => item?.video_index === targetIndex
                                ) || null;

                              const targetIsLive = !!targetProgramme?.is_live_now;
                              const targetAlreadyPlaying =
                                targetIsLive &&
                                targetRow?.id === currentChannelId &&
                                targetVideo?.id === currentVideo?.id;

                              if (targetAlreadyPlaying) {
                                onClose?.();
                              } else if (targetIsLive || !entry?.target_video_id) {
                                onSelect?.(targetRow.id);
                              } else {
                                onSelect?.(targetRow.id, targetIndex);
                              }
                            }}
                            className="absolute right-2 bottom-2 z-30 border-2 border-[var(--phosphor)] bg-black/90 px-2.5 py-1 text-[8px] font-bold tracking-[0.16em] text-[var(--phosphor)] shadow-[0_0_10px_color-mix(in_srgb,var(--phosphor)_48%,transparent)] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
                            data-testid={`guide-full-featured-watch-${index}`}
                          >
                            ▶ WATCH NOW
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedFullGuideFeaturedEntry && (
                  <div
                    className="mt-2 border-t border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] pt-2"
                    data-testid="guide-full-featured-information"
                  >
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <div className="text-[8px] tracking-[0.18em] text-[var(--phosphor)] opacity-72">
                        ★ FEATURED INFORMATION
                      </div>
                      <div className="text-[11px] md:text-[12px] font-semibold chromatic leading-tight">
                        {selectedFullGuideFeaturedEntry.title ||
                          selectedFullGuideRow?.name ||
                          "FEATURED"}
                      </div>
                    </div>

                    <p className="mt-1 text-[10px] md:text-[11px] leading-[1.4] opacity-76 line-clamp-2">
                      {selectedFullGuideFeaturedSynopsis}
                    </p>
                  </div>
                )}
              </div>
            )}

            {!fullGuideOperatorOpen && fullGuideDiscoveryOpen && selectedFullGuideRow?.playlist?.length > 0 && (
              <div
                className="xl:col-start-1 xl:row-start-1 min-w-0 border-t border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] pt-2"
                data-testid="guide-smart-discovery-strip"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {featuredEntries.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setFullGuideDiscoveryMode("featured")}
                        className="border border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] px-3 py-1 text-[9px] tracking-[0.18em] hover:border-[var(--phosphor)]"
                        data-testid="guide-full-featured-toggle"
                        aria-pressed="false"
                      >
                        ★ FEATURED
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setFullGuideDiscoveryMode("programmes")}
                      className="border border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_14%,transparent)] text-[var(--phosphor)] px-3 py-1 text-[9px] tracking-[0.18em] phosphor-glow"
                      data-testid="guide-full-programmes-toggle"
                      aria-pressed="true"
                    >
                      ▦ PROGRAMMES
                    </button>
                    <span className="hidden md:inline text-[8px] tracking-[0.12em] opacity-42 truncate">
                      {selectedFullGuideRow.name} · {selectedFullGuideRow.playlist.length} AVAILABLE
                    </span>
                  </div>
                  <div className="text-[8px] tracking-[0.14em] opacity-48">
                    FOLLOWS HIGHLIGHTED CHANNEL
                  </div>
                </div>

                <div className="flex gap-2 overflow-x-auto scrollbar-crt pb-1" data-testid="guide-full-programmes">
                  {fullGuideProgrammeShowSeriesSelector ? (
                    fullGuideProgrammeSeriesGroups.map((group) => {
                      const firstVideo = group.videos[0] || null;
                      const selected =
                        Number(selectedFullGuideVideo?.season_number) === group.seriesNumber;

                      const art =
                        firstVideo?.backdrop_url ||
                        firstVideo?.poster_url ||
                        selectedFullGuideRow?.channel?.thumbnail_url ||
                        selectedFullGuideRow?.channel?.guide_poster_url ||
                        (firstVideo?.kind === "youtube" && firstVideo?.youtube_id
                          ? `https://i.ytimg.com/vi/${firstVideo.youtube_id}/hqdefault.jpg`
                          : null) ||
                        (firstVideo?.kind === "archive" && firstVideo?.archive_id
                          ? `https://archive.org/services/img/${firstVideo.archive_id}`
                          : null);

                      const seriesLabel =
                        group.seriesNumber === 0
                          ? "SPECIALS"
                          : `SERIES ${group.seriesNumber}`;

                      return (
                        <button
                          key={`series-${group.seriesNumber}`}
                          type="button"
                          onClick={() => setFullGuideProgrammeSeries(group.seriesNumber)}
                          className={`relative shrink-0 w-[12.5rem] h-[5.6rem] overflow-hidden border text-left transition-all ${
                            selected
                              ? "border-2 border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] shadow-[0_0_14px_color-mix(in_srgb,var(--phosphor)_22%,transparent)]"
                              : "border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/55 hover:border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)]"
                          }`}
                          data-testid={`guide-full-series-${group.seriesNumber}`}
                          aria-current={selected ? "true" : undefined}
                        >
                          {art && (
                            <img
                              src={art}
                              alt=""
                              draggable="false"
                              className={`absolute inset-0 w-full h-full object-cover ${
                                selected ? "opacity-42" : "opacity-24"
                              }`}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          )}

                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/72 to-black/28" />

                          <div className="relative z-10 h-full p-2.5 flex flex-col justify-between">
                            <div className="flex items-start justify-between gap-2 text-[7px] tracking-[0.13em]">
                              <span className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-black/65 px-1.5 py-0.5">
                                {seriesLabel}
                              </span>

                              {selected && (
                                <span className="opacity-75">● CURRENT</span>
                              )}
                            </div>

                            <div>
                              <div className={`leading-tight ${
                                selected ? "text-[13px] chromatic" : "text-[12px]"
                              }`}>
                                {seriesLabel}
                              </div>

                              <div className="mt-1 text-[8px] tracking-[0.13em] opacity-58">
                                {group.videos.length} EPISODE{group.videos.length === 1 ? "" : "S"}
                              </div>

                              <div className="mt-1 text-[7px] tracking-[0.14em] opacity-72">
                                SELECT TO BROWSE →
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <>
                      {fullGuideProgrammeNeedsSeriesChoice && (
                        <button
                          type="button"
                          onClick={() => setFullGuideProgrammeSeries(null)}
                          className="relative shrink-0 w-[7.5rem] h-[5.6rem] overflow-hidden border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] bg-black/65 hover:border-[var(--phosphor)] text-left transition-all"
                          data-testid="guide-full-series-back"
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/35" />
                          <div className="relative z-10 h-full p-2 flex flex-col justify-between">
                            <div className="text-[7px] tracking-[0.14em] opacity-48">
                              PROGRAMMES
                            </div>
                            <div>
                              <div className="text-[12px] leading-tight chromatic">
                                ← SERIES
                              </div>
                              <div className="mt-1 text-[7px] tracking-[0.12em] opacity-55">
                                BACK
                              </div>
                            </div>
                          </div>
                        </button>
                      )}

                      {fullGuideProgrammeVisibleVideos.map(({ video, index }, railIndex) => {
                        const selected = index === selectedFullGuideIndex;
                        const parts = fullGuideProgrammeParts(video);
                        const episodeCode =
                          video?.season_number != null && video?.episode_number != null
                            ? `S${String(video.season_number).padStart(2, "0")}E${String(video.episode_number).padStart(2, "0")}`
                            : (String(video?.title || "").match(/\\b(S\\d{1,2}E\\d{1,3})\\b/i)?.[1]?.toUpperCase() || null);

                        const art =
                          video?.backdrop_url ||
                          video?.poster_url ||
                          selectedFullGuideRow?.channel?.thumbnail_url ||
                          selectedFullGuideRow?.channel?.guide_poster_url ||
                          (video?.kind === "youtube" && video?.youtube_id
                            ? `https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg`
                            : null) ||
                          (video?.kind === "archive" && video?.archive_id
                            ? `https://archive.org/services/img/${video.archive_id}`
                            : null);

                        const episodeNumber =
                          video?.episode_number != null
                            ? Number(video.episode_number)
                            : railIndex + 1;

                        return (
                          <button
                            key={video.id || `${selectedFullGuideRow.id}-${index}`}
                            ref={(node) => {
                              if (node && video?.id) fullGuideDiscoveryRefs.current.set(video.id, node);
                              else if (video?.id) fullGuideDiscoveryRefs.current.delete(video.id);
                            }}
                            type="button"
                            onClick={() => {
                              setFullGuideOffset(index - selectedFullGuideRow.nowIndex);
                              setFullGuideTimeAnchorMs(null);
                            }}
                            className={`relative shrink-0 w-[10.8rem] h-[5.6rem] overflow-hidden border text-left transition-all ${
                              selected
                                ? "border-2 border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] shadow-[0_0_14px_color-mix(in_srgb,var(--phosphor)_22%,transparent)]"
                                : "border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/55 hover:border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)]"
                            }`}
                            data-testid={`guide-full-programme-${index}`}
                            aria-current={selected ? "true" : undefined}
                          >
                            {art && (
                              <img
                                src={art}
                                alt=""
                                draggable="false"
                                className={`absolute inset-0 w-full h-full object-cover ${
                                  selected ? "opacity-42" : "opacity-24"
                                }`}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            )}

                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/72 to-black/28" />

                            <div className="relative z-10 h-full p-2 flex flex-col justify-between">
                              <div className="flex items-start justify-between gap-2 text-[7px] tracking-[0.12em]">
                                <span className="opacity-58 tabular-nums">
                                  #{String(episodeNumber).padStart(2, "0")}
                                </span>
                                {episodeCode && <span className="opacity-70">{episodeCode}</span>}
                              </div>

                              <div>
                                {parts.eyebrow && (
                                  <div className="text-[7px] tracking-[0.1em] opacity-55 truncate mb-0.5">
                                    {parts.eyebrow}
                                  </div>
                                )}

                                <div className={`line-clamp-2 leading-tight ${
                                  selected ? "text-[11px] chromatic" : "text-[10px]"
                                }`}>
                                  {parts.title}
                                </div>

                                {selected && (
                                  <div className="mt-1 text-[7px] tracking-[0.16em] opacity-80">
                                    ▶ SELECTED
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      {/* Search results — replaces the rails/preview split when the
          operator is actively searching. Grouped: channels first (they
          take the eye instantly), then tapes with a "CH XX · Channel
          Name" subtitle so the operator can eyeball which channel a
          matching tape lives on. Empty-state message when nothing
          matches. Click any row to tune. Keyboard: ↑/↓ Enter Esc.
          Nothing renders when searchQ is empty — the normal rails
          come back the moment the query clears. */}
      {searchQ.length >= 1 ? (
        <div
          data-testid="guide-search-results"
          data-search-surface="premium"
          className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(340px,0.9fr)_minmax(0,1.35fr)] gap-3 overflow-hidden"
        >
          <div
            data-testid="guide-search-browser"
            className="min-h-0 flex flex-col overflow-hidden border border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] bg-black/55"
          >
            <div className="shrink-0 px-3 py-2 border-b border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] flex items-end justify-between gap-3">
              <div>
                <div className="text-[8px] tracking-[0.28em] opacity-45">DISCOVERY SEARCH</div>
                <div className="text-[19px] leading-none tracking-[0.1em]" style={{ fontFamily: "VT323, monospace" }}>
                  RESULTS FOR "{searchQuery}"
                </div>
              </div>
              <div className="text-[8px] tracking-[0.18em] opacity-45">
                {searchResults.flat.length} MATCH{searchResults.flat.length === 1 ? "" : "ES"}
              </div>
            </div>

            {searchResults.flat.length === 0 ? (
              <div
                data-testid="guide-search-empty"
                className="flex-1 flex items-center justify-center p-6 text-center"
              >
                <div>
                  <div className="text-[34px] leading-none opacity-25" style={{ fontFamily: "VT323, monospace" }}>
                    NO SIGNAL
                  </div>
                  <div className="mt-2 text-[10px] tracking-[0.18em] opacity-55">
                    NO MATCHES FOR "{searchQuery}"
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto scrollbar-crt p-2 space-y-1">
                {searchResults.flat.map((hit, idx) => {
                  const active = idx === searchIndex;
                  const art = searchHitArtwork(hit);
                  const meta = searchHitMeta(hit);
                  return (
                    <button
                      key={hit.id}
                      type="button"
                      onClick={() => commitSearchHit(hit)}
                      onMouseEnter={() => setSearchIndex(idx)}
                      data-testid={
                        hit.kind === "channel"
                          ? `guide-search-hit-channel-${hit.channel.id}`
                          : `guide-search-hit-video-${hit.video.id}`
                      }
                      aria-current={active ? "true" : undefined}
                      className={`w-full min-h-[76px] grid grid-cols-[96px_minmax(0,1fr)] text-left border overflow-hidden transition-all duration-150 ${
                        active
                          ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_13%,black)] shadow-[0_0_18px_color-mix(in_srgb,var(--phosphor)_16%,transparent)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)] bg-black/30 hover:border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)]"
                      }`}
                    >
                      <div className="relative min-h-[74px] bg-black overflow-hidden">
                        {art ? (
                          <img
                            src={art}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover opacity-85"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-[26px] opacity-18" style={{ fontFamily: "VT323, monospace" }}>
                            {hit.kind === "channel" ? "CH" : "TV"}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" />
                        <div className="absolute left-1.5 bottom-1 px-1.5 py-0.5 text-[7px] tracking-[0.18em] bg-black/75 border border-white/10">
                          {searchHitType(hit)}
                        </div>
                      </div>

                      <div className="min-w-0 p-2.5 self-center">
                        <div className="truncate text-[14px] tracking-[0.06em] font-semibold">
                          {searchHitTitle(hit)}
                        </div>
                        {meta.length > 0 && (
                          <div className="mt-1 truncate text-[9px] tracking-[0.12em] opacity-48">
                            {meta.join(" · ")}
                          </div>
                        )}
                        <div className={`mt-2 text-[8px] tracking-[0.16em] ${active ? "opacity-80" : "opacity-30"}`}>
                          {active
                            ? (hit.kind === "channel" ? "ENTER · TUNE CHANNEL" : "ENTER · TUNE IN")
                            : "MOVE TO PREVIEW"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="shrink-0 px-3 py-2 border-t border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] text-[8px] tracking-[0.15em] opacity-38 flex gap-4 flex-wrap">
              <span>↑ ↓ BROWSE</span>
              <span>ENTER TUNE</span>
              <span>ESC CLEAR</span>
            </div>
          </div>

          <div
            data-testid="guide-search-preview"
            className="relative min-h-[280px] lg:min-h-0 overflow-hidden border border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] bg-black"
          >
            {searchActiveHit ? (
              <>
                {searchHitArtwork(searchActiveHit, true) && (
                  <img
                    src={searchHitArtwork(searchActiveHit, true)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-28"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/82 to-black/38" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />

                <div className="relative z-10 h-full p-4 md:p-5 flex flex-col">
                  <div className="flex items-center justify-between gap-3 shrink-0">
                    <div className="text-[8px] tracking-[0.28em] opacity-45">QUICK PREVIEW</div>
                    <div className="px-2 py-1 text-[8px] tracking-[0.16em] border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] bg-black/50 opacity-65">
                      {searchHitType(searchActiveHit)}
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_150px] gap-4 items-end">
                    <div className="min-w-0 pb-1">
                      <div className="mb-2 text-[10px] tracking-[0.16em] opacity-48">
                        {searchHitMeta(searchActiveHit).join(" · ")}
                      </div>

                      <div
                        data-testid="guide-search-preview-title"
                        className="text-[clamp(34px,4vw,58px)] leading-[0.9] tracking-[0.04em] phosphor-glow"
                        style={{ fontFamily: "VT323, monospace" }}
                      >
                        {searchHitTitle(searchActiveHit)}
                      </div>

                      <div
                        data-testid="guide-search-preview-description"
                        className="mt-4 max-w-2xl text-[11px] md:text-[12px] leading-[1.6] opacity-70 line-clamp-5"
                      >
                        {searchHitDescription(searchActiveHit)}
                      </div>

                      <button
                        type="button"
                        data-testid="guide-search-preview-tune"
                        onClick={() => commitSearchHit(searchActiveHit)}
                        className="mt-5 px-4 py-2 border border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_14%,black)] text-[10px] tracking-[0.2em] shadow-[0_0_18px_color-mix(in_srgb,var(--phosphor)_16%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_22%,black)]"
                      >
                        ENTER · {searchActiveHit.kind === "channel" ? "TUNE CHANNEL" : "TUNE IN"}
                      </button>
                    </div>

                    <div className="hidden md:block self-end">
                      <div className="aspect-[2/3] overflow-hidden border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] bg-black/70">
                        {searchHitArtwork(searchActiveHit) ? (
                          <img
                            src={searchHitArtwork(searchActiveHit)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-center p-3">
                            <div>
                              <div className="text-[30px] opacity-18" style={{ fontFamily: "VT323, monospace" }}>SLTV</div>
                              <div className="text-[7px] tracking-[0.18em] opacity-25">ARTWORK UNAVAILABLE</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Normal rails/preview split — hidden when a search is active
          so the results panel gets the full viewport height. */}
      {/* Phase 2.3.61e.5-r4 — FULL GUIDE TMDb artwork modal.
          This must live outside the hidden legacy CHANNELS render branch;
          otherwise the FULL GUIDE operator button can set open=true while
          the modal component itself is never mounted. */}
      <TmdbChannelArtworkModal
        key={`guide-tmdb-art-${fullGuideOperatorChannel?.id || "none"}-${selectedFullGuideVideo?.id || "none"}`}
        open={fullGuideTmdbArtworkOpen && !!fullGuideOperatorChannel && !fullGuideOperatorChannel.virtual && !fullGuideOperatorChannel.isSubChannel}
        initialQuery={
          selectedFullGuideVideo?.show_title ||
          selectedFullGuideVideo?.title ||
          fullGuideOperatorChannel?.name ||
          ""
        }
        targetId={fullGuideOperatorChannel?.id}
        apiBase={apiBase}
        authHeaders={authHeaders}
        onClose={() => setFullGuideTmdbArtworkOpen(false)}
        onApplied={async () => {
          if (onChannelArtChanged) await onChannelArtChanged();
        }}
      />

      {/* Phase 2.3.61e.6.1 — legacy CHANNELS render tree retired.
          Owner artwork/TMDb controls now live in FULL GUIDE's ⚙ CHANNEL shelf. */}

      <div
        className="mt-3 text-[10px] opacity-70 text-center border-t border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] pt-2 tracking-[0.08em]"
        data-testid="guide-navigation-hints"
      >
        &gt; ←→ TIME &nbsp;·&nbsp; ↑↓ CHANNEL &nbsp;·&nbsp; ENTER TUNE &nbsp;·&nbsp; GUIDE / ESC EXIT
      </div>
    </div>
  );
}
